/**
 * Educator Dashboard
 * v14
 * SEL tracking dashboard for teachers using Where the Brave Things Grow.
 *
 * Sidebar: My Classes · Growth · My Notes · My Well-Being
 *
 * My Classes: class cards → click → Class Detail view (check-ins + observations inside)
 * Growth: unchanged from v3
 * My Notes: personal teacher notepad with timestamps
 * My Well-Being: 3-question visual scale check-in (Energy, Mood, Connection)
 *
 * NOTE: The inline "Add note" per domain inside check-in form is preserved exactly from v3.
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  BookOpen, Users, ClipboardCheck, Eye, TrendingUp,
  LogOut, Plus, Save, ChevronRight, Sparkles, Leaf,
  Rainbow, Shield, Heart, Star, Droplets, Sun,
  MessageCircle, X, CheckCircle2, Circle, MinusCircle,
  BarChart3, Calendar, ArrowLeft, Pencil, Trash2,
  NotebookPen, HeartPulse, Zap, Smile, Anchor, BookMarked
} from "lucide-react";
import { Link } from "react-router-dom";

// ─── Types ────────────────────────────────────────────────────────────────────

interface EducatorClass {
  id: string;
  class_name: string;
  grade_level?: string;
  school_name?: string;
  academic_year: string;
  student_count?: number;
}

interface CheckIn {
  id: string;
  class_id: string;
  checkin_date: string;
  checkin_period: string;
  emotional_awareness: number;
  self_regulation: number;
  courage: number;
  kindness: number;
  gratitude: number;
  sharing_repair: number;
  mattering: number;
  notes?: string;
  relationship_snapshot?: string;
}

interface Observation {
  id: string;
  class_id: string;
  observation_date: string;
  domain: string;
  quote?: string;
  context?: string;
}

interface TeacherNote {
  id: string;
  content: string;
  created_at: string;
}

interface WellBeingEntry {
  id: string;
  energy: number;      // 1-5
  stress: number;      // 1-5
  mood: number;        // 1-5
  connection: number;  // 1-5
  note?: string;
  created_at: string;
}

// ─── Domain Config ─────────────────────────────────────────────────────────────

const DOMAINS = [
  {
    key: "emotional_awareness",
    label: "Colorful Chameleon",
    subtitle: "Emotional Awareness",
    icon: Rainbow,
    color: "from-purple-100 to-pink-100",
    border: "border-purple-200",
    iconColor: "text-purple-500",
    badgeColor: "bg-purple-100 text-purple-700",
    description: "Names feelings, understands emotional states"
  },
  {
    key: "self_regulation",
    label: "Still Pond",
    subtitle: "Self-Regulation & Mindfulness",
    icon: Droplets,
    color: "from-blue-100 to-cyan-100",
    border: "border-blue-200",
    iconColor: "text-blue-500",
    badgeColor: "bg-blue-100 text-blue-700",
    description: "Uses calm-down tools, stays regulated"
  },
  {
    key: "courage",
    label: "Brave Bridge",
    subtitle: "Courage & Approach",
    icon: Shield,
    color: "from-orange-100 to-amber-100",
    border: "border-orange-200",
    iconColor: "text-orange-500",
    badgeColor: "bg-orange-100 text-orange-700",
    description: "Takes brave steps, tries hard things"
  },
  {
    key: "kindness",
    label: "Kindness Meadow",
    subtitle: "Kindness & Inclusion",
    icon: Heart,
    color: "from-rose-100 to-pink-100",
    border: "border-rose-200",
    iconColor: "text-rose-500",
    badgeColor: "bg-rose-100 text-rose-700",
    description: "Kind words, includes others, helps"
  },
  {
    key: "gratitude",
    label: "Gratitude Garden",
    subtitle: "Gratitude & Positive Focus",
    icon: Sun,
    color: "from-yellow-100 to-amber-100",
    border: "border-yellow-200",
    iconColor: "text-yellow-600",
    badgeColor: "bg-yellow-100 text-yellow-700",
    description: "Expresses thanks, notices good things"
  },
  {
    key: "sharing_repair",
    label: "Sharing Tree",
    subtitle: "Sharing & Repair",
    icon: Leaf,
    color: "from-green-100 to-emerald-100",
    border: "border-green-200",
    iconColor: "text-green-600",
    badgeColor: "bg-green-100 text-green-700",
    description: "Shares, takes turns, attempts repair"
  },
  {
    key: "mattering",
    label: "Mattering",
    subtitle: "Feeling Seen & Valued",
    icon: Sparkles,
    color: "from-violet-100 to-indigo-100",
    border: "border-violet-200",
    iconColor: "text-violet-500",
    badgeColor: "bg-violet-100 text-violet-700",
    description: "Feels their presence and voice matter"
  },
];

const DOMAIN_OPTIONS = [
  { value: 1, label: "Not yet",   icon: Circle,      color: "text-gray-400 border-gray-200 bg-white hover:bg-gray-50" },
  { value: 2, label: "Sometimes", icon: MinusCircle, color: "text-golden-600 border-golden-300 bg-golden-50 hover:bg-golden-100" },
  { value: 3, label: "Often",     icon: CheckCircle2,color: "text-forest-600 border-forest-300 bg-forest-50 hover:bg-forest-100" },
];

// ─── Well-Being Scale Config ───────────────────────────────────────────────────

const WELLBEING_SCALES = [
  {
    id: "energy",
    label: "Energy Level",
    icon: Zap,
    color: "text-yellow-500",
    low: "Exhausted",
    high: "Energized",
    emojis: ["😴", "😔", "😐", "🙂", "⚡"],
  },
  {
    id: "stress",
    label: "Stress Level",
    icon: HeartPulse,
    color: "text-red-400",
    low: "Very stressed",
    high: "At ease",
    emojis: ["😰", "😟", "😐", "😌", "🧘"],
  },
  {
    id: "mood",
    label: "Mood Level",
    icon: Smile,
    color: "text-rose-500",
    low: "Overwhelmed",
    high: "Joyful",
    emojis: ["😟", "😕", "😶", "😊", "😄"],
  },
  {
    id: "connection",
    label: "Social Connection",
    icon: Anchor,
    color: "text-blue-500",
    low: "Disconnected",
    high: "Deeply connected",
    emojis: ["🫥", "😶", "🙂", "🤝", "💚"],
  },
];

// ─── Sidebar Nav ───────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { id: "classes",    label: "My Classes",          icon: Users,       desc: "Manage your classes" },
  { id: "growth",     label: "Student Growth",       icon: TrendingUp,  desc: "Progress over time" },
  { id: "wellbeing",  label: "My Readiness Meter",   icon: HeartPulse,  desc: "How ready are you today?" },
  { id: "readiness",  label: "My Readiness Score",   icon: BarChart3,   desc: "Your readiness over time" },
  { id: "curriculum", label: "Dira Book Curriculum", icon: BookMarked,  desc: "18-week classroom curriculum" },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function generateId() {
  return `edu_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function getDomainLabel(key: string) {
  return DOMAINS.find(d => d.key === key)?.label || key;
}

function getRatingLabel(val: number) {
  return val === 1 ? "Not yet" : val === 2 ? "Sometimes" : "Often";
}

function getRatingColor(val: number) {
  return val === 1 ? "text-gray-500" : val === 2 ? "text-golden-600" : "text-forest-600";
}

// ─── Curriculum Data V3 ──────────────────────────────────────────────────────

const CURRICULUM_CHAPTERS = [
  // ── Chapter 1: The Still Pond ────────────────────────────────────────────
  {
    title: "The Still Pond",
    domain: "Mindfulness & Self-Regulation",
    emoji: "🐸",
    cue: "Pond Breath",
    character: "Wise Frog",
    border: "border-blue-200",
    badge: "bg-blue-100 text-blue-700",
    bigIdea: "Being still on the outside helps us feel steady on the inside. When our bodies and minds get busy or overwhelmed, we can always return to the pond — a quiet place inside us.",
    youMatter: "Your pond matters. How you feel on the inside matters.",
    tactiletouchpoints: [
      "Hand on belly — feel it rise and fall with each breath",
      "Slow exhale through the mouth — like gently blowing on a pond to make ripples",
    ],
    tactileNote: "These become the physical language of this domain. By Week 3 a teacher can use the gesture silently from across the room and kids know exactly what it means.",
    goalsKids: [
      "I can notice when my body feels busy or wobbly inside.",
      "I can take a pond breath to help myself feel steadier.",
      "My grown-ups can take pond breaths too — we can do it together.",
    ],
    goalsTeachers: [
      "Students will practice recognizing activation in their bodies (wiggly, tight, racing).",
      "Students will use the pond breath as a shared classroom regulation tool.",
      "Teacher will model using the pond breath for themselves, not just directing kids to use it.",
    ],
    structured: {
      week1intro: "Meet the character, learn the cue, introduce the tactile touchpoints, set up the physical classroom anchor. The teacher's job this week is to make the language feel natural — not taught.",
      week1: [
        "Monday (20–25 min): Read the chapter on the platform — all spreads, animations, videos. Do the Cue activity (Pond Breath — both paths: expressive chant and quiet still breath). One question only after reading: the big one from the story. Let it breathe. Introduce the 2 tactile touchpoints: hand on belly + slow exhale. Practice once together. Send Home Bridge Week 1 home today.",
        "Tuesday (10–15 min): Brief re-read of 2–3 key spreads (kids pick). Science Connection conversationally: 'Did you know your brain actually changes when you take a pond breath? Three slow breaths tells your whole body: we're safe.' Body check-in: 'Put your hand on your belly. Is your pond still or splashy right now?'",
        "Wednesday (10 min): Activities Page core activity: Pond Breathing steps done together. Set up the classroom Pond Breath Corner — the physical anchor for this domain. Teacher adds the first entry: one moment this week when they needed a pond breath. Model it being real, not performative.",
        "Thursday (natural flow): Use the language in real moments throughout the day. One touchpoint: 'I noticed some splashy ponds during math this morning — we took a breath together. That's your Steady Oak showing up.'",
        "Friday (5–10 min): Quick share circle: 'What's one thing from this chapter that stuck with you?' Preview the Home Bridge: 'This weekend, try a pond breath at home with your family. Tell them Wise Frog taught you.'",
      ],
      week2intro: "Deepening = Practice + Exploration + Metacognition. Kids and teacher co-own the domain together. The teacher doesn't prescribe — they facilitate. The goal, the tracking method, and the 'when do we use this?' conversation all come from the class.",
      week2framework: "Monday: Reiterate the concept, facilitate class discussion — when do we use this? Kids generate the answers.\n\nTuesday: Teacher and students set a group goal together — how many times will we practice? How will we track it? Kids co-create.\n\nWed & Thu: The goal is live. Teacher watches for real moments.\n\nFriday: Assess at teacher's discretion. Metacognition question: did you reach for it without being asked?",
      week2: [
        "Monday (15–20 min): Reiterate the concept — briefly, not a re-read. Bring the language and touchpoint back alive: 'Remember pond breaths? This week we're going to really USE them.' Discuss implementation together: 'When should we be doing pond breaths? Where did you notice them showing up last week?' Kids generate the answers.",
        "Tuesday (10–15 min): Set the group goal together. Teacher provides the framework: 'How many times do you think we could practice as a class this week? How will we track it?' Kids co-create the goal — the number, the tracking method, the artifact. Make the goal visible and celebratory — not competitive.",
        "Wednesday & Thursday (natural flow): The goal is live. The corner is there. Teacher watches for real moments to use the language. When a genuine moment happens — a conflict, pre-test nerves, a loud recess return — use the pond breath then. Narrate progress naturally: 'We just added three more to our corner — we're getting closer.'",
        "Friday (5–10 min): Assess and reflect as a class circle: 'Did we hit our goal? How did it feel? What was hard?' For ages 3–5: thumbs up/middle/down. For ages 5–7: 'one word for how pond breaths felt this week.' Metacognition: 'Did you notice yourself reaching for a pond breath without being asked? That's your Steady Oak growing.'",
      ],
      week3intro: "No new content. No new activities. The teacher stops teaching and starts being. The Three Moves are the only framework needed.",
      week3moves: "Move 1 — Notice & Name: When the domain shows up live in the classroom, the teacher names it. Not a lesson — just a sentence. The teacher is a mirror reflecting back what's already happening.\n\nMove 2 — Claim Your Own: The teacher uses the domain language for themselves, out loud, in real moments. Not demonstrating — actually using it. This is what makes it feel real to kids.\n\nMove 3 — Open the Door: Once per day, the teacher offers a brief no-pressure invitation for kids to use the language themselves. Kids who want to walk through it will. Kids who don't, don't have to.",
      week3: [
        "'Do we need a pond breath before we start?'",
        "'I notice some splashy ponds in here — let's take one together.'",
        "'My pond needed a breath just now. Did yours?'",
        "'I saw you take a pond breath on your own. That's your Steady Oak showing up.'",
      ],
    },
    flexible: {
      week1intro: "Read The Still Pond. Your only goal this week is to let the language feel natural — not scheduled.",
      week1: [
        "Start with the story: Read Chapter 1 on the platform and let Wise Frog do the teaching. One pond breath together after — no setup needed.",
        "Look for live moments: Name the pond when it shows up naturally. 'My pond is a bit splashy this morning.' Your noticing gives kids permission to notice too.",
        "One light touchpoint: A single body check-in sometime this week — 'Is your pond still or splashy right now?' That's it.",
        "Plant one seed: 'Wise Frog lives inside all of us.' Once, wherever it fits.",
      ],
      week2intro: "This week is about letting kids start owning the language. Stay present, stay real.",
      week2framework: "Facilitate, don't prescribe. Let kids name when they use it, set their own goal, and choose how to track it. Your job is to watch for real moments and use the language yourself.",
      week2: [
        "Both paths are valid: Some kids will chant and breathe big. Others will go still and quiet. Honor both without announcing it.",
        "Let a child lead: Find a moment to ask a kid to lead the class in a pond breath. You'll know when it's right.",
        "Use something real: If something activating happens this week — conflict, recess return, nerves — that's the moment. Not a planned time.",
        "Send it home: End of week, one line to families: 'We've been practicing pond breaths. Here's how to try it at home.'",
      ],
      week3intro: "No new content. Just use the language wherever it fits this week.",
      week3moves: "Move 1 — Notice & Name: When the domain shows up, name it. One sentence. No lesson.\n\nMove 2 — Claim Your Own: Use the language for yourself, in real moments — not as a demonstration.\n\nMove 3 — Open the Door: One no-pressure invitation per day. Kids who want to walk through it will.",
      week3: [
        "'Do we need a pond breath before we start?'",
        "'I notice some splashy ponds in here — let's take one together.'",
        "'My pond needed a breath just now. Did yours?'",
        "'I saw you take a pond breath on your own. That's your Steady Oak showing up.'",
      ],
    },
    doThis: [
      "Take pond breaths yourself, out loud, in front of kids — model it as something adults need too.",
      "Name the sensation without judgment: 'Your pond might feel splashy and that's completely okay.'",
      "Use it at transition moments, before tests, after recess — not just during Dira time.",
      "Celebrate quiet self-regulation: 'I noticed you took a pond breath before you raised your hand.'",
    ],
    notThis: [
      "Don't use it as a consequence: 'You need to take a pond breath' (punitive tone kills the tool).",
      "Don't skip it when things feel calm — that's the best time to build the habit.",
      "Don't force participation — offer the quiet path for kids who aren't ready to chant.",
      "Don't rush it. Three slow breaths is three slow breaths, not a quick inhale/exhale.",
    ],
    homeBridge: [
      {
        week: "Week 1 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Read Chapter 1 together on the platform. Just the story — let Wise Frog do the work. No activities tonight.", dare: "Notice once tonight when your own pond feels splashy. You don't have to do anything about it yet — just notice." },
          { day: "Tuesday evening", mission: "Print the Pond Breathing Cards and set up your calm-down corner together. Find a quiet spot, make it cozy, let your child lead the decorating. (Context: your child learned today that three slow breaths shifts the brain from alarm to calm — the corner gives that a home.)", dare: "Put a Pond Breathing Card somewhere YOU will see it. Not just your child — you." },
          { day: "Wednesday evening", mission: "Two conversation starters for dinner or bedtime: 'When do you feel scattered or overwhelmed like Mila did?' / 'What do you notice when you take slow, deep breaths?'", dare: "Answer the same questions yourself, honestly, with your child. Let your answer be real." },
          { day: "Thursday evening", mission: "Two more conversation starters: 'How did breathing help the friends feel better?' / 'What are some times when our family could use pond breathing together?'", dare: "Let your child answer first. Really listen before you respond." },
          { day: "Friday / Weekend", mission: "Use the calm-down corner together once — not because anyone is upset, just to practice when it's easy.", dare: "This weekend, use the pond breath out loud when YOUR pond gets splashy. Let your child hear you do it. That's the whole dare." },
        ],
      },
      {
        week: "Week 2 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Your child set a class goal today. Ask them about it: 'What is your class working toward this week? Can we help from home?'", dare: "Ask your child to show you the pond breath exactly how they do it at school. Let them be the teacher tonight." },
          { day: "Wednesday evening", mission: "Practice pond breathing together once — before dinner, at bedtime, whenever. Just once. Use the calm-down corner if it feels right.", dare: "Use the pond breath yourself at a real moment this week — commute, making dinner, before a hard conversation. Not for your child. For you." },
          { day: "Friday / Weekend", mission: "Ask your child: 'Did your class hit their goal this week?' Celebrate whatever the number is — every breath counts.", dare: "This weekend, when something frustrating happens, use the pond breath before you respond. Notice what shifts — in you and in the room." },
        ],
      },
      {
        week: "Week 3 Home Bridge",
        days: [
          { day: "This week", mission: "No new activity. Just keep the language alive naturally. When you hear 'splashy pond' or 'pond breath' — reflect it back.", conversation: ["When did your Steady Oak show up this week?", "What's one thing that feels different about how we talk about feelings now?"], dare: "Write it down or just hold it — remember one moment this week where you saw your child's Steady Oak show up. You'll want to remember this." },
        ],
      },
    ],
    reflection: [
      "Did I use the pond breath for myself this week, not just as a direction to kids?",
      "What moments in our day did I find myself naturally reaching for it?",
      "Which kids seemed most helped by it — and which kids might need the quiet path instead?",
    ],
  },

  // ── Chapter 2: The Colorful Chameleon ────────────────────────────────────
  {
    title: "The Colorful Chameleon",
    domain: "Emotional Awareness",
    emoji: "🦎",
    cue: "Rainbow Tail",
    character: "Camille, Pip, Bella",
    border: "border-purple-200",
    badge: "bg-purple-100 text-purple-700",
    bigIdea: "Every feeling has a color. Naming what we feel — or just pointing to a color that matches — helps us understand our inner world. All feelings are valid, even the ones that surprise us.",
    youMatter: "Every color of yours is real. Your feelings matter.",
    tactiletouchpoints: [
      "Point to a color in the room that matches how you feel right now",
      "Hand on chest — where does that feeling live in your body?",
    ],
    tactileNote: "These become the physical language of this domain. By Week 3 a teacher can use the gesture silently from across the room and kids know exactly what it means.",
    goalsKids: [
      "I can notice a feeling happening inside me.",
      "I can name it OR find a color that feels like it — both count.",
      "Even wiggly, mixed-up feelings are okay and normal.",
    ],
    goalsTeachers: [
      "Students will expand their emotional vocabulary beyond happy/sad/mad.",
      "Students will practice noticing feelings without needing to immediately fix them.",
      "Teacher will respond to emotional expression with curiosity, not correction.",
    ],
    structured: {
      week1intro: "Meet the character, learn the cue, introduce the tactile touchpoints, set up the physical classroom anchor. The teacher's job this week is to make the language feel natural — not taught.",
      week1: [
        "Monday (20–25 min): Read Chapter 2 on the platform — all spreads, animations, videos. Do the Rainbow Tail cue (both paths: 'I Feel It, I Name It' and 'Color Drift'). One question after: 'Camille changes colors for her feelings. If YOUR feelings showed up in colors today, what color would you be?' Let it breathe. Introduce the 2 tactile touchpoints: point to a color + hand on chest. Send Home Bridge Week 1 home today.",
        "Tuesday (10–15 min): Brief re-read of 2–3 key spreads (focus on Camille explaining feelings). Science Connection: 'Did you know that just naming a feeling out loud actually calms your brain down? Saying I feel worried makes the worry smaller.' Body check-in: 'Point to a color in this room that matches how you feel right now. You don't have to explain it — just point.'",
        "Wednesday (10 min): Activities Page core activity: Chameleon Feelings Chart — draw and color together as a class. Set up the class feelings color reference — kids choose what color matches which feeling. No right answers. Teacher adds their own color to the chart today. Model emotional honesty.",
        "Thursday (natural flow): Use color language in real moments throughout the day. One touchpoint: 'I can see some different colors in here right now — that's okay, all colors belong in this room.'",
        "Friday (5–10 min): Quick share: 'What color are you bringing out of this week?' Preview Home Bridge: 'This weekend, try pointing to a color that matches how you feel and show someone at home.'",
      ],
      week2intro: "Deepening = Practice + Exploration + Metacognition. Kids and teacher co-own the domain together.",
      week2framework: "Monday: Reiterate, facilitate class discussion — when do we check in with colors? Kids generate the moments.\n\nTuesday: Set group goal together — how many color check-ins this week? Kids co-create the goal and tracking method.\n\nWed & Thu: Goal is live. Color check-ins in real moments. Honor Color Drift as fully as verbal naming.\n\nFriday: Assess. Metacognition: did you reach for it without being asked?",
      week2: [
        "Monday (15–20 min): Reiterate: bring Rainbow Tail language and touchpoint back alive. Discuss implementation: 'When should we be checking in with our colors? When did you notice your color changing last week?' Introduce the polka-dotted pizza idea if it hasn't landed yet: you can feel MORE than one color at once.",
        "Tuesday (10–15 min): Set the group goal together — how many color check-ins could we do as a class this week? Kids co-create the goal and the tracking method (Chameleon Chart, color tally, whatever they decide). Make it visible, celebratory, and non-competitive.",
        "Wednesday & Thursday (natural flow): Goal is live. Color check-ins happen in real moments — transitions, after something activating, start of day. Honor Color Drift (pointing) as fully as verbal naming — say it out loud once this week. Narrate progress naturally.",
        "Friday (5–10 min): Assess as a class: 'Did we hit our goal? What colors showed up most this week?' Metacognition: 'Did you notice yourself reaching for a color without being asked? That's your Rainbow Tail growing.'",
      ],
      week3intro: "No new content. No new activities. The teacher stops teaching and starts being. The Three Moves are the only framework needed.",
      week3moves: "Move 1 — Notice & Name: When the domain shows up live in the classroom, the teacher names it. Not a lesson — just a sentence.\n\nMove 2 — Claim Your Own: The teacher uses the domain language for themselves, out loud, in real moments. Not demonstrating — actually using it.\n\nMove 3 — Open the Door: Once per day, the teacher offers a brief no-pressure invitation for kids to use the language themselves.",
      week3: [
        "'What color are you bringing into the room right now?'",
        "'I can see some different colors in here today — all colors belong.'",
        "'Your Rainbow Tail is really showing right now — I see that.'",
        "'You don't have to name it. Just notice it. That's enough.'",
      ],
    },
    flexible: {
      week1intro: "Read The Colorful Chameleon. Let the color language land in the room before you do anything formal with it.",
      week1: [
        "One question after the read: 'If your feelings showed up in colors today, what color would you be?' Let it breathe. No right answers.",
        "Name your own color when it comes up naturally — in the car line, at the start of the day, wherever. When you do it, kids will too.",
        "One light touchpoint: Ask kids to point to a color nearby that matches how they feel. No words required. Pointing counts.",
        "Plant the seed: 'You can feel MORE than one color at once — that's not confusing, that's just real.' Once, and move on.",
      ],
      week2intro: "Your job this week is to keep sharing your own color and trust that kids are watching.",
      week2framework: "Facilitate, don't prescribe. Let kids name when they use color check-ins, set their own goal, and choose how to track it.",
      week2: [
        "Share yours first: One or two real color moments from you this week — not a lesson, just honesty.",
        "Body bridge: When a color comes up, occasionally ask where they feel it in their body. Not every time.",
        "Honor the quiet path: Notice who points instead of naming. Say once: 'You don't have to name it.' That gives quiet kids permission.",
        "Send it home: End of week, one line to families: 'We're using color language for feelings. Here's how to try at home.'",
      ],
      week3intro: "Color check-ins are just part of how the classroom breathes now. No scheduling needed.",
      week3moves: "Move 1 — Notice & Name: When a feeling shows up in the room, name the color. One sentence.\n\nMove 2 — Claim Your Own: Share your color in real moments — not as a model, as a person.\n\nMove 3 — Open the Door: One soft invitation per day for kids to share theirs.",
      week3: [
        "'What color are you bringing into the room right now?'",
        "'I can see some different colors in here today — all colors belong.'",
        "'Your Rainbow Tail is really showing right now — I see that.'",
        "'You don't have to name it. Just notice it. That's enough.'",
      ],
    },
    doThis: [
      "Accept whatever color a child offers without trying to change or fix it.",
      "Name your own color regularly so kids see adults have feelings too.",
      "Use the color language during conflicts: 'What color were you feeling when that happened?'",
      "Honor the Color Drift path — some kids aren't ready to name feelings aloud and that's valid.",
    ],
    notThis: [
      "Don't tell kids what color they should be: 'You should be feeling yellow today!'",
      "Don't use the check-in as surveillance: 'You said red — do you need to talk to someone?'",
      "Don't rush to fix a hard color. Sit with it first.",
      "Don't skip the mixed feelings concept — it's one of the most powerful things in this chapter.",
    ],
    homeBridge: [
      {
        week: "Week 1 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Read Chapter 2 together on the platform. Ask just one question after: 'If your feelings showed up in colors today, what color would you be?'", dare: "Notice once tonight what color YOU might be. You don't have to share it — just notice." },
          { day: "Tuesday evening", mission: "Print the Chameleon Feelings Chart and put it somewhere visible — fridge, bedroom wall, wherever your family gathers. Let your child color it to show how they feel today.", dare: "Add your own color to the chart. Let your child see that adults have colors too." },
          { day: "Wednesday evening", mission: "Two conversation starters: 'If you were a chameleon, what color would you be right now? Why?' / 'What helps you feel better when you're upset?'", dare: "Share your own color at a natural moment. No explanation needed — just honest." },
          { day: "Thursday evening", mission: "Two more conversation starters: 'Can you remember a time today when you had a big feeling?' / 'Who can you talk to when you have a strong feeling?'", dare: "When a hard color comes up tonight, don't rush to fix it. Sit with it first. Say: 'I see that color. Tell me more.'" },
          { day: "Friday / Weekend", mission: "Use the Chameleon Chart together once — each person colors in how they feel and shares one word.", dare: "This weekend, respond to one of your child's hard colors with curiosity instead of comfort. Try 'tell me about that color' instead of 'it'll be okay.'" },
        ],
      },
      {
        week: "Week 2 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Ask about the class goal: 'What is your class tracking this week? Can we help from home?'", dare: "Share your color with your child once this week at a real moment. Let it be genuine." },
          { day: "Wednesday evening", mission: "Use the Chameleon Chart together — each person picks a color and shares one word. No right answers.", dare: "Try the body connection: 'Where do you feel that color in your body?' Ask your child. Then answer it yourself." },
          { day: "Friday / Weekend", mission: "Check in on the class goal — celebrate whatever the number is.", dare: "This week, when a hard feeling comes up for you — name the color out loud. Let your child hear what honest emotional awareness sounds like in an adult." },
        ],
      },
      {
        week: "Week 3 Home Bridge",
        days: [
          { day: "This week", mission: "No new activity. Just keep using the color language naturally when feelings show up.", conversation: ["What color are you bringing to the dinner table tonight?", "Did you notice any color shifts in yourself today?"], dare: "This week, respond to your child's hard colors with curiosity at least once. No fixing. Just witnessing." },
        ],
      },
    ],
    reflection: [
      "Did I respond to kids' hard colors with curiosity rather than trying to fix them?",
      "Which kids used the verbal path vs. the color drift path — and what does that tell me?",
      "Did I share my own color this week? How did kids respond?",
    ],
  },

  // ── Chapter 3: The Brave Bridge ──────────────────────────────────────────
  {
    title: "The Brave Bridge",
    domain: "Courage",
    emoji: "🦔",
    cue: "Bravery Badge",
    character: "Pip, Bella, Max",
    border: "border-orange-200",
    badge: "bg-orange-100 text-orange-700",
    bigIdea: "Bravery isn't the absence of fear — it's trying something hard while feeling scared, and knowing you are cared for in that moment. One small step IS bravery.",
    youMatter: "Your courage matters — even the tiny steps no one else sees.",
    tactiletouchpoints: [
      "Balance on one leg — the wobble IS the bravery (for ages 3–5: one big step forward)",
      "Say 'I can try!' out loud or whispered — both count equally",
    ],
    tactileNote: "These become the physical language of this domain. By Week 3 a teacher can use the gesture silently from across the room and kids know exactly what it means.",
    goalsKids: [
      "Sometimes I can feel scared and still take a brave step.",
      "My body might feel wiggly or tight, and that's okay — that's bravery getting ready.",
      "Grown-ups can help me be brave, and I can help others be brave too.",
    ],
    goalsTeachers: [
      "Students will practice identifying fear and nervousness in their bodies.",
      "Students will attempt small brave steps with support, at their own pace.",
      "Teacher will respond to fear and effort with curiosity and care, not dismissal.",
    ],
    structured: {
      week1intro: "Meet the character, learn the cue, introduce the tactile touchpoints, set up the physical classroom anchor. The teacher's job this week is to make the language feel natural — not taught.",
      week1: [
        "Monday (20–25 min): Read Chapter 3 on the platform — all spreads, animations, videos. Do the Bravery Badge cue (both paths: 'I Can Try!' and 'Courage Check-In'). One question after: 'What was brave about what Pip did — even the parts that didn't go perfectly?' Let kids wrestle with it. Introduce the tactile touchpoints: balance on one leg + 'I can try!' The wobble is the point. Send Home Bridge Week 1 home today.",
        "Tuesday (10–15 min): Brief re-read of 2–3 key spreads (focus on Bella helping Pip at the bridge). Science Connection: 'Did you know that every time you try something scary your brain actually builds new connections? Your bravery muscles are real — and they grow every time you try.' Body awareness: 'What does your body feel like right before you try something new? Wiggly tummy? Fast heartbeat? That's bravery getting ready.'",
        "Wednesday (10 min): Activities Page core activity: Brave Steps Challenge — make a 'bridge' in the classroom (tape line, mat, low step). Take turns crossing while saying 'I can try!' Let kids choose their own brave level. Set up the Bravery Board. Teacher adds first entry: one small brave thing they tried this week. Make it real.",
        "Thursday (natural flow): Name bravery when it shows up live — not in the story, in the room. One touchpoint: 'I noticed you tried that hard problem again after you got it wrong. That's a brave step — and I saw it.'",
        "Friday (5–10 min): Quick share: 'What was one brave step from this week — yours or someone else's you noticed?' Preview Home Bridge: 'This weekend, try a brave step at home and tell your family about it.'",
      ],
      week2intro: "Deepening = Practice + Exploration + Metacognition. Kids and teacher co-own the domain together.",
      week2framework: "Monday: Reiterate, facilitate — where does bravery show up in our day? Surface the effort vs. outcome distinction.\n\nTuesday: Set group goal together — how many brave steps on the Bravery Board this week? Kids co-create.\n\nWed & Thu: Goal is live. Effort language only — 'I noticed you tried' not 'I noticed you succeeded.'\n\nFriday: Assess. Metacognition: did you say 'I can try' without being reminded?",
      week2: [
        "Monday (15–20 min): Reiterate: 'Remember brave steps? This week we're going to really USE them.' Discuss implementation: 'Where did you notice bravery last week? When does it show up in our day?' Surface the effort vs. outcome distinction: 'Is it braver to succeed at something scary, or to try even when you might not?'",
        "Tuesday (10–15 min): Set the group goal: 'How many brave steps could we collect on our Bravery Board this week? How do we want to track them?' Kids co-create the goal and tracking method — badges, tally, stars, whatever they decide. Clarify: brave steps can be academic, social, physical — anything counts.",
        "Wednesday & Thursday (natural flow): Goal is live. Bravery Board is there. Teacher names specific brave steps when they show up. Effort language only: 'I noticed you tried' not 'I noticed you succeeded.' Both cue paths available.",
        "Friday (5–10 min): Assess: 'Did we hit our goal? What brave steps showed up that surprised you?' Metacognition: 'Did you notice yourself saying I can try without being reminded? That's your Brave Thing growing.'",
      ],
      week3intro: "No new content. No new activities. The teacher stops teaching and starts being. The Three Moves are the only framework needed.",
      week3moves: "Move 1 — Notice & Name: When the domain shows up live in the classroom, the teacher names it. Not a lesson — just a sentence.\n\nMove 2 — Claim Your Own: The teacher uses the domain language for themselves, out loud, in real moments. Not demonstrating — actually using it.\n\nMove 3 — Open the Door: Once per day, the teacher offers a brief no-pressure invitation for kids to use the language themselves.",
      week3: [
        "'I noticed you tried that even though it felt hard. That's your Brave Thing showing up.'",
        "'Your tummy might feel wiggly — that's bravery getting ready.'",
        "'Do you want to try I can try! or the Courage Check-In first?'",
        "'You took a brave step. Whether it went perfectly or not — you tried.'",
      ],
    },
    flexible: {
      week1intro: "Read The Brave Bridge. Let Pip's fear land before you do anything with it.",
      week1: [
        "One question after the read: 'What was brave about what Pip did — even the parts that didn't go perfectly?' Let it sit.",
        "Name bravery when you see it live in the room — not in the story. Quietly and specifically.",
        "Put up a Bravery Board without a big launch. Add your own first entry. Kids will notice.",
        "Plant the seed: 'A wiggly tummy before something new? That's bravery getting ready.' Once, when the moment is right.",
      ],
      week2intro: "This week is about building effort-focused language through real moments — not lessons.",
      week2framework: "Facilitate, don't prescribe. Let kids name when bravery shows up, set their own goal, and choose how to track it.",
      week2: [
        "Name one specific brave step per day — not general praise, something you actually saw. Specificity is what makes it land.",
        "Raise the question naturally: 'Is it braver to succeed, or to try even when you might not?' No right answer needed.",
        "Use both paths yourself — 'I can try!' when something feels hard, the Courage Check-In when something feels overwhelming.",
        "Send it home: 'We're practicing brave steps. Effort over outcome. Here's how to try at home.'",
      ],
      week3intro: "Bravery language is just how the classroom talks now. No lesson needed.",
      week3moves: "Move 1 — Notice & Name: When courage shows up, name it. One sentence.\n\nMove 2 — Claim Your Own: Use bravery language for yourself in real moments.\n\nMove 3 — Open the Door: One soft daily invitation for kids to name their own brave steps.",
      week3: [
        "'I noticed you tried that even though it felt hard. That's your Brave Thing showing up.'",
        "'Your tummy might feel wiggly — that's bravery getting ready.'",
        "'Do you want to try I can try! or the Courage Check-In first?'",
        "'You took a brave step. Whether it went perfectly or not — you tried.'",
      ],
    },
    doThis: [
      "Share your own small brave stories: 'I felt nervous about this too, and I tried anyway.'",
      "Break challenges into tiny steps and let kids choose their own pace.",
      "Acknowledge fear directly: 'It makes complete sense to feel scared before trying something new.'",
      "Celebrate effort loudly: 'I noticed you tried' — not just 'you did it.'",
    ],
    notThis: [
      "Don't push kids into scary situations before they're ready.",
      "Don't dismiss fear: 'There's nothing to be scared of' or 'you're being silly.'",
      "Don't compare: 'Your friend isn't scared — why are you?'",
      "Don't focus only on outcomes: 'See, that wasn't scary!' Name the courage it took to try.",
    ],
    homeBridge: [
      {
        week: "Week 1 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Read Chapter 3 together. One question: 'What brave thing did a character do, even though they were scared?'", dare: "Notice once tonight when YOU face something a little scary or unfamiliar. You don't have to name it yet — just notice." },
          { day: "Tuesday evening", mission: "Print the Brave Steps Badges and set up a family Bravery Board — a wall, a poster, anywhere. Add your own first entry: one small brave thing YOU tried this week.", dare: "Let your child see you add to the Bravery Board. Make your entry real, not performative." },
          { day: "Wednesday evening", mission: "Two conversation starters: 'What was your brave step today, even if it felt small?' / 'What's something you'd like to try that feels a little scary?'", dare: "Share a time you felt scared but tried anyway. Be specific — not a big story, just a real moment." },
          { day: "Thursday evening", mission: "Two more conversation starters: 'How did your body feel before and after trying something new?' / 'Who helped you be brave, or who could you help next time?'", dare: "When your child is hesitant tonight, resist fixing it. Try: 'It makes sense to feel scared. Your body is getting ready to be brave.'" },
          { day: "Friday / Weekend", mission: "Do the Brave Steps Challenge at home together — make a simple 'bridge' and take turns crossing while saying 'I can try!'", dare: "This weekend, let your child see you face something nerve-wracking. Name it out loud: 'This feels scary for me — I'm going to try anyway.' Just visibly trying." },
        ],
      },
      {
        week: "Week 2 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Ask about the class goal: 'What is your class working toward this week? What counts as a brave step?'", dare: "Once this week, celebrate your child's effort specifically — not the outcome. 'I saw you try. That's the brave thing — whether it worked or not.'" },
          { day: "Wednesday evening", mission: "Add to the family Bravery Board together — one brave step each person took this week.", dare: "When your child is scared this week, resist the urge to push or dismiss. Try waiting. Let them find their own step." },
          { day: "Friday / Weekend", mission: "Check in on the class goal — celebrate whatever happened.", dare: "This weekend, name the courage it took for something your child tried — regardless of result. 'That took guts. I saw that.'" },
        ],
      },
      {
        week: "Week 3 Home Bridge",
        days: [
          { day: "This week", mission: "No new activity. Keep noticing and naming bravery when it shows up — in your child, in yourself.", conversation: ["What brave step did someone in our family take this week?", "Is it braver to succeed or to try?"], dare: "This week, celebrate one effort over one outcome. Find the moment and name it." },
        ],
      },
    ],
    reflection: [
      "Did I respond differently when kids were scared or hesitant this week?",
      "Did I use more effort-focused language ('you tried') than outcome-focused ('you did it')?",
      "What's one phrase from this chapter that I heard myself saying naturally?",
    ],
  },

  // ── Chapter 4: The Kindness Meadow ───────────────────────────────────────
  {
    title: "The Kindness Meadow",
    domain: "Kindness",
    emoji: "🌸",
    cue: "Sparkling Petal",
    character: "Gentle Deer, Max, Bella, Mila",
    border: "border-rose-200",
    badge: "bg-rose-100 text-rose-700",
    bigIdea: "Kindness is like a ripple in a pond — one small act spreads far and wide. And kindness doesn't need an audience: it can be loud and sparkly, or quiet and private. Both count.",
    youMatter: "Your kindness matters — including the quiet kind that no one applauds.",
    tactiletouchpoints: [
      "Raise your hand and say 'Kindness Spark!' — then turn toward someone",
      "A quiet hand gesture toward someone — kindness without words",
    ],
    tactileNote: "These become the physical language of this domain. By Week 3 a teacher can use the gesture silently from across the room and kids know exactly what it means.",
    goalsKids: [
      "I can do or say something kind — big or small, out loud or quietly.",
      "Kindness to myself is just as real as kindness to others.",
      "When I notice someone's kindness, naming it makes it bigger.",
    ],
    goalsTeachers: [
      "Students will practice kindness as both a public act and a private intention.",
      "Students will begin to notice and name kindness when they observe it in others.",
      "Teacher will model self-kindness out loud — not just kindness to others.",
    ],
    structured: {
      week1intro: "Meet the character, learn the cue, introduce the tactile touchpoints, set up the physical classroom anchor. The teacher's job this week is to make the language feel natural — not taught.",
      week1: [
        "Monday (20–25 min): Read Chapter 4 on the platform — all spreads, animations, videos. Do the Sparkling Petal cue (both paths: 'Kindness Spark!' and 'Quiet Acts'). One question after: 'What was the smallest kind thing someone did in that story — the thing that might have seemed like nothing?' Let it sit. Introduce tactile touchpoints: raise hand + 'Kindness Spark!' + turn toward someone. Send Home Bridge Week 1 home today.",
        "Tuesday (10–15 min): Brief re-read of 2–3 key spreads (focus on Gentle Deer's ripple moment). Science Connection: 'Did you know that being kind actually makes YOUR brain feel good too? It releases a happiness chemical — kindness is a happiness boomerang.' Body check-in: 'When you do something kind — even just think about it — where do you feel it in your body?'",
        "Wednesday (10 min): Activities Page core activity: Kindness Ripple Experiment — bowl of water, drop a pebble, watch ripples. Talk about one kind act that could ripple in your class. Set up the Kindness Chain or Ripple Wall in the classroom. Teacher adds first link: one specific kindness they witnessed today.",
        "Thursday (natural flow): Name kindness when it shows up live — specifically, not generically. One touchpoint: 'I saw you move over so she could sit down. That's a sparkling petal — I noticed that.'",
        "Friday (5–10 min): Quick share: 'What's one kindness you noticed this week — yours or someone else's?' Preview Home Bridge: 'This weekend, try a Kindness Spark at home — or a Quiet Act. Both count.'",
      ],
      week2intro: "Deepening = Practice + Exploration + Metacognition. Kids and teacher co-own the domain together.",
      week2framework: "Monday: Reiterate, facilitate — where does kindness show up in our day? Surface self-kindness.\n\nTuesday: Set group goal — how many kindness sparks to notice and name this week? Kids co-create.\n\nWed & Thu: Goal is live. Name specific kindnesses when they show up. Honor Quiet Acts explicitly.\n\nFriday: Assess. Metacognition: did you notice yourself doing something kind without being asked?",
      week2: [
        "Monday (15–20 min): Reiterate: 'Remember kindness sparks? This week we're going to notice them everywhere — and track them.' Discuss implementation: 'Where does kindness show up in our day? What does it look like when it's quiet vs. loud?' Surface self-kindness: 'Can you be kind to yourself? What does that look like?'",
        "Tuesday (10–15 min): Set the group goal: 'How many kindness sparks could we notice and name as a class this week? How do we want to track them?' Kids co-create goal and tracking method — the Kindness Chain, a tally, whatever they decide. Clarify: quiet acts count fully. A kind thought counts.",
        "Wednesday & Thursday (natural flow): Goal is live. Kindness Chain is there. Name specific kindnesses when they show up. Model witnessing language: 'I saw that. Sparkling petal.' Honor Quiet Acts path explicitly once: 'You don't have to do it out loud. Kindness doesn't need an audience.'",
        "Friday (5–10 min): Assess: 'Did we hit our goal? What kinds of kindness surprised you this week?' Metacognition: 'Did you notice yourself doing something kind without being asked? That's your Warm Heart growing.'",
      ],
      week3intro: "No new content. No new activities. The teacher stops teaching and starts being. The Three Moves are the only framework needed.",
      week3moves: "Move 1 — Notice & Name: When the domain shows up live in the classroom, the teacher names it. Not a lesson — just a sentence.\n\nMove 2 — Claim Your Own: The teacher uses the domain language for themselves, out loud, in real moments. Not demonstrating — actually using it.\n\nMove 3 — Open the Door: Once per day, the teacher offers a brief no-pressure invitation for kids to use the language themselves.",
      week3: [
        "'That's a sparkling petal right there — I saw that.'",
        "'Kindness spark! Who wants to start us off?'",
        "'You don't have to say it out loud. A quiet kind act still counts.'",
        "'I'm going to do a quiet act of kindness for myself today. You might too.'",
      ],
    },
    flexible: {
      week1intro: "Read The Kindness Meadow. Your job is to start noticing kindness out loud — not organizing it.",
      week1: [
        "One question after the read: 'What was the smallest kind thing in that story — the thing that seemed like nothing?' Let it sit.",
        "Name one specific kindness each day — something you actually saw. Specificity is what makes kids start looking too.",
        "Ask once: 'What does being kind to yourself look like?' Many kids have never been asked. Let it be open.",
        "Let kindness be light: If humor and warmth show up together, name it. 'That right there — that's a kindness spark.'",
      ],
      week2intro: "This week, witnessing kindness is the skill. Practice it yourself and kids will follow.",
      week2framework: "Facilitate, don't prescribe. Let kids name when kindness shows up, set their own goal, and choose how to track it.",
      week2: [
        "Name one kindness per day — yours, a student's, anyone's. 'I saw that. Sparkling petal.' That's all it takes.",
        "Say once that quiet acts count: 'Kindness doesn't need an audience.' Watch who exhales.",
        "Model self-kindness in a real moment this week — not as a lesson, as a person.",
        "Send it home: 'We're practicing noticing kindness — not just doing it. Here's how to try at home.'",
      ],
      week3intro: "Kindness is just how this classroom speaks now. No formal practice needed.",
      week3moves: "Move 1 — Notice & Name: When kindness shows up, name it specifically.\n\nMove 2 — Claim Your Own: Use the language for yourself in real moments — including self-kindness.\n\nMove 3 — Open the Door: One soft invitation per day for kids to name what they noticed.",
      week3: [
        "'That's a sparkling petal right there — I saw that.'",
        "'Kindness spark! Who wants to start us off?'",
        "'You don't have to say it out loud. A quiet kind act still counts.'",
        "'I'm going to do a quiet act of kindness for myself today. You might too.'",
      ],
    },
    doThis: [
      "Name kindness specifically, not generically: 'I noticed you waited for him — that's a sparkling petal.'",
      "Model self-kindness out loud: 'I need to be kind to myself today.'",
      "Honor the Quiet Acts path — not all kids can express kindness publicly.",
      "Let kindness and humor coexist — Max the Mouse shows this.",
    ],
    notThis: [
      "Don't make kindness performative: 'Let's see who can be the kindest today.'",
      "Don't use kindness as behavior management: 'If you're kind you'll get a reward.'",
      "Don't skip self-kindness — kids who can't be kind to themselves struggle to sustain it toward others.",
      "Don't pressure the verbal path if Quiet Acts fits better.",
    ],
    homeBridge: [
      {
        week: "Week 1 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Read Chapter 4 together. One question: 'What was the smallest kind thing someone did — the thing that might have seemed like nothing?'", dare: "Notice one kind thing your child does tonight — big or small. Name it specifically: 'I saw that. That was a sparkling petal.'" },
          { day: "Tuesday evening", mission: "Start a family Kindness Chain — cut paper strips, add a link every time someone does something kind. Hang it somewhere visible.", dare: "Add your own link first. Let your child see you do it." },
          { day: "Wednesday evening", mission: "Two conversation starters: 'Who did you help today, or who helped you?' / 'How do you think kindness spreads in our family?'", dare: "Do a kind act for yourself out loud where your child can see it. Show what self-kindness looks like in an adult." },
          { day: "Thursday evening", mission: "Two more conversation starters: 'How did it feel to do something kind for someone else?' / 'What's one kind thing you'd like to try tomorrow?'", dare: "Try a Quiet Act of kindness — something you do without telling anyone. Notice how it feels." },
          { day: "Friday / Weekend", mission: "Do the Kindness Ripple Experiment at home — bowl of water, pebble, ripples. Talk about one kind act that rippled in your family this week.", dare: "This weekend, model self-kindness out loud once — a real moment. 'I need to be kind to myself right now.' Let your child hear what that sounds like." },
        ],
      },
      {
        week: "Week 2 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Ask about the class goal: 'What is your class tracking this week? What counts as a kindness spark?'", dare: "Once this week, name a specific kindness you witnessed in your child. Not general praise — something you actually saw." },
          { day: "Wednesday evening", mission: "Add to the Kindness Chain together — one link each for something kind that happened this week.", dare: "Practice a Quiet Act yourself this week — no audience, no announcement. Just notice how it feels." },
          { day: "Friday / Weekend", mission: "Check in on the class goal — celebrate whatever happened.", dare: "This weekend, model self-kindness out loud in a real moment. Let your child witness it." },
        ],
      },
      {
        week: "Week 3 Home Bridge",
        days: [
          { day: "This week", mission: "No new activity. Keep noticing and naming kindness when it shows up naturally.", conversation: ["What's the smallest kind thing someone did this week?", "Did anyone do a quiet act of kindness nobody saw?"], dare: "This week, model self-kindness out loud once more — let it become a pattern your child starts to expect." },
        ],
      },
    ],
    reflection: [
      "Did I name kindness specifically this week — not generically?",
      "Did I model self-kindness out loud? How did that land?",
      "Which kids gravitated toward Quiet Acts — and what might that be telling me?",
    ],
  },

  // ── Chapter 5: The Gratitude Garden ──────────────────────────────────────
  {
    title: "The Gratitude Garden",
    domain: "Gratitude",
    emoji: "🐢",
    cue: "Gratitude Leaf",
    character: "Grandpa Tortoise",
    border: "border-yellow-200",
    badge: "bg-yellow-100 text-yellow-700",
    bigIdea: "Gratitude isn't about pretending everything is good. It's about noticing what IS good, even on a hard day. Grandpa Tortoise teaches us to slow down and let goodness land in our bodies, not just our heads.",
    youMatter: "What you notice and feel grateful for — that inner world — matters.",
    tactiletouchpoints: [
      "Make a heart shape with both hands and say 'I'm grateful for ___'",
      "Touch something nearby and name what you appreciate about it",
    ],
    tactileNote: "These become the physical language of this domain. By Week 3 a teacher can use the gesture silently from across the room and kids know exactly what it means.",
    goalsKids: [
      "I can notice one good thing, even on a hard day.",
      "Gratitude can be a feeling in my body — not just a word I say.",
      "Saying thank you and feeling thankful are both real kinds of gratitude.",
    ],
    goalsTeachers: [
      "Students will practice gratitude as a somatic (body-felt) experience, not just a verbal one.",
      "Students will distinguish between forced positivity and genuine noticing.",
      "Teacher will model authentic, specific gratitude — including gratitude for small, ordinary things.",
    ],
    structured: {
      week1intro: "Meet the character, learn the cue, introduce the tactile touchpoints, set up the physical classroom anchor. The teacher's job this week is to make the language feel natural — not taught.",
      week1: [
        "Monday (20–25 min): Read Chapter 5 on the platform — all spreads, animations, videos. Do the Gratitude Leaf cue (both paths: 'Leaf of Thanks' and 'Warm Memory'). One question after: 'Grandpa Tortoise moves slowly and notices everything. What's something in this room right now that you almost never notice?' Pause. Wait. The noticing IS the practice. Introduce tactile touchpoints: heart shape + touch something and name gratitude for it. Send Home Bridge Week 1 home today.",
        "Tuesday (10–15 min): Brief re-read of 2–3 key spreads (focus on Grandpa Tortoise's slowness). Science Connection: 'Did you know your brain is like a treasure hunter? When you practice noticing good things, your brain gets better at finding MORE good things every day.' Warm Memory practice together: 'Close your eyes or let your gaze go soft. Picture a moment that felt warm or safe. Just let it be there for a few seconds. Where do you feel that in your body?'",
        "Wednesday (10 min): Activities Page core activity: make Gratitude Leaves together — write or draw one thing each person is grateful for. Set up the Gratitude Garden in the classroom — branch, wall space, jar, whatever works. Teacher adds first leaf: something small and genuinely real, not 'my family' — something specific like 'the way the light came in this morning.'",
        "Thursday (natural flow): Use gratitude language in real moments — specifically and unhurried. One touchpoint: 'Before we switch — what's one tiny thing from this morning that you'd put in the Gratitude Garden?'",
        "Friday (5–10 min): Quick share: 'What's one small thing you're genuinely grateful for from this week?' Preview Home Bridge: 'This weekend, find one tiny specific thing to be grateful for and tell someone at home.'",
      ],
      week2intro: "Deepening = Practice + Exploration + Metacognition. Kids and teacher co-own the domain together.",
      week2framework: "Monday: Reiterate, facilitate — when does gratitude show up? Distinguish genuine from forced.\n\nTuesday: Set group goal — how many leaves in the Gratitude Garden this week? Kids co-create.\n\nWed & Thu: Goal is live. Model micro-gratitude. Offer the Warm Memory path at a calm moment. If hard moments come — let them be hard.\n\nFriday: Assess. Metacognition: did you reach for gratitude without being asked?",
      week2: [
        "Monday (15–20 min): Reiterate: 'Remember the Gratitude Garden? This week we're going to really FILL it — and practice noticing tiny things, not just big ones.' Discuss: 'When does gratitude show up in our day? What does it feel like in your body? When is it hard to feel grateful?' Distinguish genuine from forced: 'There's a difference between I have to be grateful and I notice something good. Can you feel the difference?'",
        "Tuesday (10–15 min): Set the group goal together: 'How many leaves could we add to the Gratitude Garden this week?' Kids co-create goal and method. Make the bar accessible — a grateful thought counts, not just a big feeling.",
        "Wednesday & Thursday (natural flow): Goal is live. Garden is there. Teacher models micro-gratitude in real moments. Offer the Warm Memory path at a calm moment: 'Close your eyes for 10 seconds. Picture something that felt good. Just let it be there.' If a hard moment comes up — let it be hard. Don't rush to gratitude.",
        "Friday (5–10 min): Assess: 'Did we fill the garden? What tiny things showed up that surprised you?' Metacognition: 'Did you notice yourself reaching for gratitude without being asked? That's your Grateful Heart growing.'",
      ],
      week3intro: "No new content. No new activities. The teacher stops teaching and starts being. The Three Moves are the only framework needed.",
      week3moves: "Move 1 — Notice & Name: When the domain shows up live in the classroom, the teacher names it. Not a lesson — just a sentence.\n\nMove 2 — Claim Your Own: The teacher uses the domain language for themselves, out loud, in real moments. Not demonstrating — actually using it.\n\nMove 3 — Open the Door: Once per day, the teacher offers a brief no-pressure invitation for kids to use the language themselves.",
      week3: [
        "'Before we switch — what's one thing from this morning you'd put in the Gratitude Garden?'",
        "'Grandpa Tortoise pace: slow down for 10 seconds and notice something good.'",
        "'Your Grateful Heart is showing. I can see it.'",
        "'It doesn't have to be big. The tiny things count.'",
      ],
    },
    flexible: {
      week1intro: "Read The Gratitude Garden. Let Grandpa Tortoise's pace be the whole lesson this week.",
      week1: [
        "One question after the read: 'What's something in this room you almost never notice?' Pause. Wait. The noticing is the practice.",
        "Model micro-gratitude when it's real — something tiny and specific, not 'I'm grateful for my family.' Specific and unhurried.",
        "A Gratitude Garden or jar in the room — no big launch. Add your first leaf. Let kids add theirs when they feel moved to.",
        "Plant the seed: 'Gratitude isn't pretending hard things aren't hard. It's noticing what's also true.' Once, and leave it.",
      ],
      week2intro: "This week is about building the habit of small, specific noticing — body-felt, not just verbal.",
      week2framework: "Facilitate, don't prescribe. Let kids name when gratitude shows up, set their own goal, and choose how to track it.",
      week2: [
        "Go small and specific: Model one micro-gratitude out loud. Then invite kids: 'What's one small thing from today that felt good, even for a second?'",
        "Warm Memory practice: At a calm moment, 'Close your eyes. Picture a moment that felt safe or warm. Just let it be there.' No debrief needed.",
        "Let hard days be hard: If something genuinely difficult happens, don't rush to gratitude.",
        "Send it home: 'We're practicing noticing small good things — not forced positivity. Here's how to try at home.'",
      ],
      week3intro: "Gratitude language is just woven into the day now — transitions, closings, quiet moments.",
      week3moves: "Move 1 — Notice & Name: When something good shows up, name it. One sentence.\n\nMove 2 — Claim Your Own: Share your own genuine gratitude in real moments — small and specific.\n\nMove 3 — Open the Door: One soft invitation per day for kids to notice something good.",
      week3: [
        "'Before we switch — what's one thing from this morning you'd put in the Gratitude Garden?'",
        "'Grandpa Tortoise pace: slow down for 10 seconds and notice something good.'",
        "'Your Grateful Heart is showing. I can see it.'",
        "'It doesn't have to be big. The tiny things count.'",
      ],
    },
    doThis: [
      "Practice authentic, specific gratitude — not generic positivity.",
      "Use the Warm Memory path with kids who seem emotionally flat or guarded.",
      "Let hard days be hard — gratitude doesn't mean pretending things are fine.",
      "Model micro-gratitude: small, specific, real things you genuinely notice.",
    ],
    notThis: [
      "Don't force gratitude during hard emotional moments: 'Even though you're sad, be grateful.'",
      "Don't make gratitude a performance: 'Who can tell me the most things they're grateful for?'",
      "Don't skip the Warm Memory path — for some kids it's the only door in.",
      "Don't confuse gratitude with compliance: 'Be grateful for what you have.'",
    ],
    homeBridge: [
      {
        week: "Week 1 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Read Chapter 5 together. One question: 'What's something small Grandpa Tortoise noticed that you might have walked right past?'", dare: "Share a micro-gratitude out loud tonight — something tiny and specific. Not 'I'm grateful for my family' but something real like 'I'm grateful it stopped raining this morning.'" },
          { day: "Tuesday evening", mission: "Print the Gratitude Leaves and set up a family Gratitude Garden — branch, wall space, jar. Each person adds one leaf. Let your child's be whatever they choose.", dare: "Add your own leaf first. Make it specific and real." },
          { day: "Wednesday evening", mission: "Two conversation starters: 'What was your favorite thing that happened today?' / 'What's something small that made you smile today?'", dare: "Tonight, let a hard moment just be hard. Don't rush to gratitude. If it feels right later, gently offer: 'Something hard and something good can both be true at the same time.'" },
          { day: "Thursday evening", mission: "Two more conversation starters: 'Who is someone you're grateful for and why?' / 'How does it feel when someone says thank you to you?'", dare: "Try the Warm Memory yourself tonight — picture a moment that felt safe or warm, let it settle in your body for a few seconds. You don't have to share it." },
          { day: "Friday / Weekend", mission: "Start a bedtime gratitude moment together — just once this weekend. Each person shares one specific small thing they appreciated today.", dare: "This weekend, model authentic gratitude — specific, small, real. Not performed. Let your child see what genuine noticing looks like." },
        ],
      },
      {
        week: "Week 2 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Ask about the class goal: 'What is your class tracking this week? What counts as a gratitude leaf?'", dare: "Once this week, share a micro-gratitude with your child at a real moment — small, specific, genuine." },
          { day: "Wednesday evening", mission: "Add to the Gratitude Garden together — one leaf each for something from this week.", dare: "Practice the Warm Memory path yourself — picture a moment that felt safe or warm and sit with it. Notice what it does." },
          { day: "Friday / Weekend", mission: "Check in on the class goal — celebrate whatever the garden holds.", dare: "This weekend, let a hard moment be hard without rushing to gratitude. Then later notice if something good was also true." },
        ],
      },
      {
        week: "Week 3 Home Bridge",
        days: [
          { day: "This week", mission: "No new activity. The garden is there, the habit is forming. Keep the language alive when something genuinely good shows up.", conversation: ["What's one tiny thing from this week you almost didn't notice?", "Can you be grateful AND sad at the same time?"], dare: "Write it down or hold it — remember one moment this week where genuine gratitude showed up in your child. The small ones are worth keeping." },
        ],
      },
    ],
    reflection: [
      "Did I model specific, authentic gratitude this week — or did it feel performative?",
      "Which kids connected with Warm Memory vs. the verbal path?",
      "Did I let any hard moments just be hard this week, without rushing to gratitude?",
    ],
  },

  // ── Chapter 6: The Sharing Tree ───────────────────────────────────────────
  {
    title: "The Sharing Tree",
    domain: "Sharing & Repair",
    emoji: "🌳",
    cue: "Dew Cup",
    character: "Full ensemble",
    border: "border-green-200",
    badge: "bg-green-100 text-green-700",
    bigIdea: "Sharing isn't about giving equal parts. It's about understanding what each person truly needs and finding a way to honor those needs. When things go wrong, bridges can be rebuilt.",
    youMatter: "What you need matters. And so does what they need. Everyone matters.",
    tactiletouchpoints: [
      "Cup both hands together — hold the space for both people's needs",
      "Turn your whole body toward the other person before speaking",
    ],
    tactileNote: "These become the physical language of this domain. By Week 3 a teacher can use the gesture silently from across the room and kids know exactly what it means.",
    goalsKids: [
      "Sharing means understanding what someone else needs, not just splitting things equally.",
      "I can say what I need AND hear what someone else needs.",
      "When things go wrong, we can repair — bridges can be rebuilt.",
    ],
    goalsTeachers: [
      "Students will practice needs-based sharing: what does each person actually need here?",
      "Students will use perspective-taking as the foundation for collaboration and repair.",
      "Teacher will model repair — naming when something went wrong and how to rebuild.",
    ],
    structured: {
      week1intro: "Meet the character, learn the cue, introduce the tactile touchpoints, set up the physical classroom anchor. The teacher's job this week is to make the language feel natural — not taught.",
      week1: [
        "Monday (20–25 min): Read Chapter 6 on the platform — all spreads, animations, videos. Do the Dew Cup cue (both paths: 'Dew Cup Share' and 'Story Swap'). One question after: 'Why didn't the friends just split everything equally? What did they do instead?' Let kids wrestle with it — the discomfort IS the learning. Introduce tactile touchpoints: cupped hands + turn your whole body toward the person. Send Home Bridge Week 1 home today.",
        "Tuesday (10–15 min): Brief re-read of 2–3 key spreads (focus on the needs-based sharing moment). Science Connection: 'Did you know that when we work together to solve problems, our brains release friendship chemicals? Needs-based sharing actually makes everyone feel closer.' Body check-in: 'When you're in a conflict, where do you feel it in your body? What does it feel like when it resolves?'",
        "Wednesday (10 min): Activities Page core activity: Creative Sharing Challenge — one object between two people, each says what they need it for, brainstorm solutions together. Set up Need-It/Share-It cards in the classroom. Introduce repair language: 'Bridges can be rebuilt. What does repair look like in our classroom?'",
        "Thursday (natural flow): Use Bridge Builder language when real conflicts come up — slow them down, don't smooth them over. One touchpoint: 'Before we fix it — what does each person actually need here? Let's try a Story Swap first.'",
        "Friday (5–10 min): Quick share: 'What's one moment this week where you understood what someone else needed?' Preview Home Bridge: 'This weekend, try asking what someone needs before deciding how to share.'",
      ],
      week2intro: "Deepening = Practice + Exploration + Metacognition. Kids and teacher co-own the domain together.",
      week2framework: "Monday: Reiterate, facilitate — when do sharing conflicts show up? Surface repair.\n\nTuesday: Set group goal — how many Dew Cup moments this week? Story Swaps count. Repairs count.\n\nWed & Thu: Real conflicts are the curriculum. Story Swap before solutions every time. Model repair yourself.\n\nFriday: Assess. Metacognition: did you find yourself doing a Story Swap without being reminded?",
      week2: [
        "Monday (15–20 min): Reiterate: 'Remember the Dew Cup? This week we're going to really USE it — and try Story Swap before we try solutions.' Discuss: 'When do sharing conflicts show up in our day? What usually happens? What would be different if we understood each person's needs first?' Surface repair: 'What does rebuilding look like when something goes wrong between people?'",
        "Tuesday (10–15 min): Set the group goal: 'How many Dew Cup moments — needs conversations, Story Swaps, repair moments — could we have as a class this week?' Kids co-create the goal and tracking method. Clarify: a Story Swap counts. A repair counts. An attempted win-win counts — even if it didn't fully work.",
        "Wednesday & Thursday (natural flow): When real conflicts come up — use them. Don't smooth them over. 'Can we try a Dew Cup moment right now?' Story Swap before solutions every time. Model repair yourself: name something that went sideways and walk through rebuilding out loud.",
        "Friday (5–10 min): Assess: 'Did we hit our goal? What Dew Cup moments surprised you this week?' Metacognition: 'Did you find yourself doing a Story Swap without being reminded? That's your Bridge Builder showing up.'",
      ],
      week3intro: "No new content. No new activities. The teacher stops teaching and starts being. The Three Moves are the only framework needed.",
      week3moves: "Move 1 — Notice & Name: When the domain shows up live in the classroom, the teacher names it. Not a lesson — just a sentence.\n\nMove 2 — Claim Your Own: The teacher uses the domain language for themselves, out loud, in real moments. Not demonstrating — actually using it.\n\nMove 3 — Open the Door: Once per day, the teacher offers a brief no-pressure invitation for kids to use the language themselves.",
      week3: [
        "'Dew Cup moment — what do you need? What do they need?'",
        "'Before we solve it — can we do a Story Swap? What might they be feeling?'",
        "'Bridges can be rebuilt. What would repair look like here?'",
        "'That's a Bridge Builder move. I saw you try to understand before you reacted.'",
      ],
    },
    flexible: {
      week1intro: "Read The Sharing Tree. Let the needs-based concept land before you practice it — this idea is new for most adults too.",
      week1: [
        "One question after the read: 'Why didn't the friends split everything equally? What did they do instead?' Let kids wrestle with it. The discomfort IS the learning.",
        "Look for a real conflict this week — not manufactured. When one shows up, slow it down: 'Before we fix it — what does each person actually need here?'",
        "Introduce repair language once: 'When something goes wrong between people, it's not the end. Bridges can be rebuilt.'",
        "Try a Story Swap yourself, out loud, in a real moment: 'If I were them, I'd probably feel...' Modeling perspective-taking is the whole second half of this chapter.",
      ],
      week2intro: "This week is about using real moments — conflicts, repair, perspective-taking — as the curriculum.",
      week2framework: "Facilitate, don't prescribe. Let kids name when Dew Cup moments happen, set their own goal, and choose how to track it.",
      week2: [
        "Use a real conflict live: 'What do you need? What do they need? What might work for both?' Let the process be visible — don't smooth it over.",
        "Model repair once this week — out loud. Name what went sideways and what rebuilding looks like.",
        "Story Swap before solutions: When any disagreement surfaces, ask first: 'What do you think they might be feeling?'",
        "Send it home: 'We're practicing needs-based sharing. Here's how to try at home.'",
      ],
      week3intro: "Bridge Builder language is just how conflicts get handled in this classroom now.",
      week3moves: "Move 1 — Notice & Name: When needs-based sharing or repair shows up, name it.\n\nMove 2 — Claim Your Own: Use the language in real moments — including your own conflicts and repairs.\n\nMove 3 — Open the Door: One soft daily invitation for kids to try the Dew Cup Share or Story Swap.",
      week3: [
        "'Dew Cup moment — what do you need? What do they need?'",
        "'Before we solve it — can we do a Story Swap? What might they be feeling?'",
        "'Bridges can be rebuilt. What would repair look like here?'",
        "'That's a Bridge Builder move. I saw you try to understand before you reacted.'",
      ],
    },
    doThis: [
      "Use real conflicts as practice — don't smooth them over too quickly.",
      "Model repair yourself: name when something went sideways and show rebuilding.",
      "Emphasize Story Swap as the first move — empathy before solutions.",
      "Celebrate the attempt to understand, not just the resolution.",
    ],
    notThis: [
      "Don't default to equal splitting: 'Just give them half.' It teaches compliance, not collaboration.",
      "Don't skip repair — relationships matter more than smooth classroom management.",
      "Don't rush Story Swap to get to solutions. The perspective-taking IS the work.",
      "Don't use Dew Cup Share as a forced script. Let it be a tool kids choose.",
    ],
    homeBridge: [
      {
        week: "Week 1 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Read Chapter 6 together. One question: 'Why didn't the friends split everything equally? What did they do instead?'", dare: "Once tonight, try replacing 'just give them half' with 'what do you actually need here?' Even if it's small — the question is the practice." },
          { day: "Tuesday evening", mission: "Do the Creative Sharing Challenge at home — one object between two people, each says what they need it for, brainstorm solutions. Celebrate any win-win, however imperfect.", dare: "Let the process be messy. The attempt matters more than the resolution." },
          { day: "Wednesday evening", mission: "Two conversation starters: 'What did each character need and how did listening help?' / 'What's the difference between wants and needs?'", dare: "Tonight, model repair — name something small that went sideways and walk through what rebuilding looks like. Let your child see the whole thing." },
          { day: "Thursday evening", mission: "Two more conversation starters: 'Can you think of a time when we found a creative solution together?' / 'How does talking about needs help us feel closer?'", dare: "Before responding to a conflict tonight, try Story Swap internally: 'If I were them, I'd feel ___.' Just internally. Notice if it changes how you respond." },
          { day: "Friday / Weekend", mission: "Make Need-It/Share-It cards together and use them once this weekend when a real conflict comes up.", dare: "This weekend, try Story Swap before solutions in one real moment: 'What do you think they might be feeling?' Let that come before any fix." },
        ],
      },
      {
        week: "Week 2 Home Bridge",
        days: [
          { day: "Monday evening", mission: "Ask about the class goal: 'What is your class tracking this week? What counts as a Dew Cup moment?'", dare: "Once this week, use needs language in a real family moment: 'What do you need here? What do I need?' The attempt is enough." },
          { day: "Wednesday evening", mission: "Use the Need-It/Share-It cards together once this week when a real conflict comes up.", dare: "Model repair out loud — name something that went sideways and walk through rebuilding. Kids who see adults repair learn that it's possible." },
          { day: "Friday / Weekend", mission: "Check in on the class goal — celebrate whatever happened.", dare: "This weekend, try Story Swap before any solution: 'What do you think they might be feeling?' Just that question, first." },
        ],
      },
      {
        week: "Week 3 Home Bridge",
        days: [
          { day: "This week", mission: "No new activity. When conflicts come up this week — Story Swap before solutions. Just that question, first.", conversation: ["Did we find any win-wins this week?", "What bridge did we rebuild this week, even a small one?"], dare: "This week, before responding to a family conflict, try Story Swap internally. Notice if it changes how you show up." },
        ],
      },
    ],
    reflection: [
      "Did I use real classroom conflicts as Dew Cup practice moments this week?",
      "Did I model repair out loud — naming something that went wrong and what rebuilding looks like?",
      "Which kids struggled most with needs-based sharing? What might that tell me?",
    ],
  },
];

// ─── Main Component ────────────────────────────────────────────────────────────

export default function EducatorDashboard() {
  const { user, logout, token } = useAuth();
  const [activeTab, setActiveTab] = useState("classes");

  // ── Class data ──
  const [classes, setClasses] = useState<EducatorClass[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Class detail drill-in ──
  const [selectedClass, setSelectedClass] = useState<EducatorClass | null>(null);
  const [classCheckins, setClassCheckins] = useState<CheckIn[]>([]);
  const [classObservations, setClassObservations] = useState<Observation[]>([]);
  const [detailView, setDetailView] = useState<"list" | "checkins" | "observations">("list");

  // ── Class form ──
  const [showClassForm, setShowClassForm] = useState(false);
  const [classForm, setClassForm] = useState({ class_name: "", grade_level: "", school_name: "", student_count: "" });

  // ── Check-in form (inside class detail) ──
  const [showCheckinForm, setShowCheckinForm] = useState(false);
  const [checkinRatings, setCheckinRatings] = useState<Record<string, number>>({});
  const [checkinNotes, setCheckinNotes] = useState<Record<string, string>>({});
  const [checkinGeneralNotes, setCheckinGeneralNotes] = useState("");
  const [checkinPeriod, setCheckinPeriod] = useState("monthly");
  const [savingCheckin, setSavingCheckin] = useState(false);
  const [visibleNotes, setVisibleNotes] = useState<Record<string, boolean>>({});
  const toggleNote = (key: string) => setVisibleNotes(p => ({ ...p, [key]: !p[key] }));

  // ── Observation form (inside class detail) ──
  const [showObsForm, setShowObsForm] = useState(false);
  const [obsForm, setObsForm] = useState({ domain: "", quote: "", context: "" });
  const [savingObs, setSavingObs] = useState(false);

  // ── Growth tab — all check-ins across all classes ──
  const [allCheckins, setAllCheckins] = useState<CheckIn[]>([]);
  const [growthActiveClass, setGrowthActiveClass] = useState<EducatorClass | null>(null);

  // ── My Notes ──
  const [notes, setNotes] = useState<TeacherNote[]>([]);
  const [newNoteText, setNewNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // ── My Well-Being ──
  const [wellBeingEntries, setWellBeingEntries] = useState<WellBeingEntry[]>([]);
  const [wbRatings, setWbRatings] = useState<Record<string, number>>({});
  const [wbNote, setWbNote] = useState("");
  const [savingWb, setSavingWb] = useState(false);
  const [showWbForm, setShowWbForm] = useState(false);

  // ── Curriculum ──
  const [curriculumStyle, setCurriculumStyle] = useState<"structured" | "flexible">("flexible");
  const [activeCurriculumChapter, setActiveCurriculumChapter] = useState(0);
  const [selectedCurriculumChapter, setSelectedCurriculumChapter] = useState<number | null>(null);
  const [sendHomeModal, setSendHomeModal] = useState<{ chapter: typeof CURRICULUM_CHAPTERS[0]; week: typeof CURRICULUM_CHAPTERS[0]["homeBridge"][0] } | null>(null);

  // ─── Load on mount ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (token) loadClasses();
  }, [token]);

  useEffect(() => {
    if (token && activeTab === "growth") loadAllCheckins();
  }, [activeTab, token]);

  useEffect(() => {
    if (token && activeTab === "wellbeing") loadWellBeing();
  }, [activeTab, token]);

  // ─── API calls ────────────────────────────────────────────────────────────────

  const loadClasses = async () => {
    try {
      const res = await fetch("/api/educator/classes", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClasses(data.classes || []);
        if (data.classes?.length > 0) setGrowthActiveClass(data.classes[0]);
      }
    } catch (e) { console.error("Failed to load classes:", e); }
    finally { setLoading(false); }
  };

  const loadClassDetail = async (cls: EducatorClass) => {
    setSelectedClass(cls);
    setDetailView("list");
    try {
      const [ciRes, obsRes] = await Promise.all([
        fetch(`/api/educator/checkins/${cls.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/educator/observations/${cls.id}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (ciRes.ok) { const d = await ciRes.json(); setClassCheckins(d.checkins || []); }
      if (obsRes.ok) { const d = await obsRes.json(); setClassObservations(d.observations || []); }
    } catch (e) { console.error(e); }
  };

  const loadAllCheckins = async () => {
    if (!classes.length) return;
    try {
      const target = growthActiveClass || classes[0];
      const res = await fetch(`/api/educator/checkins/${target.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { const d = await res.json(); setAllCheckins(d.checkins || []); }
    } catch (e) { console.error(e); }
  };

  const loadWellBeing = async () => {
    try {
      const res = await fetch("/api/educator/wellbeing", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setWellBeingEntries(d.entries || []);
      }
    } catch (e) { console.error("Failed to load well-being entries:", e); }
  };

  const handleCreateClass = async () => {
    if (!classForm.class_name.trim()) { toast.error("Please enter a class name"); return; }
    try {
      const res = await fetch("/api/educator/classes", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          id: generateId(),
          class_name: classForm.class_name,
          grade_level: classForm.grade_level || undefined,
          school_name: classForm.school_name || undefined,
          student_count: classForm.student_count ? parseInt(classForm.student_count) : undefined,
          academic_year: "2025-2026"
        })
      });
      if (res.ok) {
        toast.success("Class created!");
        setShowClassForm(false);
        setClassForm({ class_name: "", grade_level: "", school_name: "", student_count: "" });
        loadClasses();
      } else toast.error("Failed to create class");
    } catch (e) { toast.error("Failed to create class"); }
  };

  const handleSaveCheckin = async () => {
    if (!selectedClass) return;
    const missing = DOMAINS.filter(d => !checkinRatings[d.key]);
    if (missing.length > 0) { toast.error(`Please rate all domains (${missing.length} remaining)`); return; }

    setSavingCheckin(true);
    try {
      const domainNoteLines = Object.entries(checkinNotes)
        .filter(([_, v]) => v.trim())
        .map(([k, v]) => `${getDomainLabel(k)}: ${v}`);
      const fullNotes = [checkinGeneralNotes, ...domainNoteLines].filter(Boolean).join("\n\n");

      const res = await fetch("/api/educator/checkins", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          id: generateId(),
          class_id: selectedClass.id,
          checkin_date: new Date().toISOString().split("T")[0],
          checkin_period: checkinPeriod,
          ...Object.fromEntries(DOMAINS.map(d => [d.key, checkinRatings[d.key] || 1])),
          notes: fullNotes || undefined,
        })
      });
      if (res.ok) {
        toast.success("Check-in saved!");
        setShowCheckinForm(false);
        setCheckinRatings({});
        setCheckinNotes({});
        setCheckinGeneralNotes("");
        setVisibleNotes({});
        loadClassDetail(selectedClass);
      } else toast.error("Failed to save check-in");
    } catch (e) { toast.error("Failed to save check-in"); }
    finally { setSavingCheckin(false); }
  };

  const handleSaveObservation = async () => {
    if (!selectedClass) return;
    if (!obsForm.domain) { toast.error("Please select a domain"); return; }
    if (!obsForm.quote && !obsForm.context) { toast.error("Please add a quote or context"); return; }

    setSavingObs(true);
    try {
      const res = await fetch("/api/educator/observations", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          id: generateId(),
          class_id: selectedClass.id,
          observation_date: new Date().toISOString().split("T")[0],
          domain: obsForm.domain,
          quote: obsForm.quote || undefined,
          context: obsForm.context || undefined,
        })
      });
      if (res.ok) {
        toast.success("Observation saved!");
        setShowObsForm(false);
        setObsForm({ domain: "", quote: "", context: "" });
        loadClassDetail(selectedClass);
      } else toast.error("Failed to save observation");
    } catch (e) { toast.error("Failed to save observation"); }
    finally { setSavingObs(false); }
  };

  const handleDeleteObservation = async (id: string) => {
    try {
      const res = await fetch(`/api/educator/observations/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setClassObservations(prev => prev.filter(o => o.id !== id));
        toast.success("Observation deleted");
      }
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleSaveNote = async () => {
    if (!newNoteText.trim()) return;
    setSavingNote(true);
    try {
      // Notes stored locally for now — no separate API endpoint needed
      const note: TeacherNote = {
        id: generateId(),
        content: newNoteText.trim(),
        created_at: new Date().toISOString(),
      };
      setNotes(prev => [note, ...prev]);
      setNewNoteText("");
      toast.success("Note saved!");
    } catch (e) { toast.error("Failed to save note"); }
    finally { setSavingNote(false); }
  };

  const handleDeleteNote = (id: string) => {
    setNotes(prev => prev.filter(n => n.id !== id));
    toast.success("Note deleted");
  };

  const handleSaveWellBeing = async () => {
    const missing = WELLBEING_SCALES.filter(s => !wbRatings[s.id]);
    if (missing.length > 0) { toast.error("Please rate all three areas"); return; }
    setSavingWb(true);
    try {
      const res = await fetch("/api/educator/wellbeing", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          id: generateId(),
          energy: wbRatings.energy,
          stress: wbRatings.stress,
          mood: wbRatings.mood,
          connection: wbRatings.connection,
          note: wbNote.trim() || undefined,
        })
      });
      if (res.ok) {
        toast.success("Well-being check-in saved!");
        setWbRatings({});
        setWbNote("");
        setShowWbForm(false);
        await loadWellBeing();
      } else {
        toast.error("Failed to save check-in");
      }
    } catch (e) { toast.error("Failed to save"); }
    finally { setSavingWb(false); }
  };

  // ─── Loading state ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 border-4 border-forest-300 border-t-forest-600 rounded-full animate-spin mx-auto" />
          <p className="text-forest-600 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-cream-50 flex flex-col">
      <style>{`
        @media print {
          header, aside, .no-print { display: none !important; }
          main { padding: 0 !important; }
          body { background: white !important; }
          #send-home-print-area { padding: 24px !important; }
        }
      `}</style>

      {/* ── Header ── */}
      <header className="bg-white border-b border-forest-100 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-forest-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-forest-900 leading-tight">Educator Dashboard</h1>
              <p className="text-xs text-forest-500">Where the Brave Things Grow</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild className="text-forest-600 hover:bg-forest-100">
              <Link to="/library">
                <BookOpen className="w-4 h-4 mr-1.5" />
                My Library
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild className="text-forest-600 hover:bg-forest-100">
              <Link to="/community">
                <MessageCircle className="w-4 h-4 mr-1.5" />
                Community
              </Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => logout()} className="text-coral-600 hover:bg-coral-100">
              <LogOut className="w-4 h-4 mr-1.5" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 max-w-7xl mx-auto w-full">

        {/* ── Sidebar ── */}
        <aside className="w-56 bg-white border-r border-forest-100 flex-shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto">
          <nav className="p-3 space-y-1">
            <div className="px-3 py-2 mb-2">
              <p className="text-xs font-semibold text-forest-400 uppercase tracking-wider">Navigation</p>
            </div>
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <div key={item.id}>
                  {item.id === "curriculum" && (
                    <div className="my-2 border-t border-forest-100" />
                  )}
                  <button
                    onClick={() => {
                      setActiveTab(item.id);
                      // Reset class detail when leaving classes tab
                      if (item.id !== "classes") setSelectedClass(null);
                      // Reset curriculum chapter when leaving curriculum tab
                      if (item.id !== "curriculum") setSelectedCurriculumChapter(null);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                      isActive
                        ? "bg-forest-600 text-white shadow-sm"
                        : "text-forest-700 hover:bg-forest-50"
                    }`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : "text-forest-500"}`} />
                    <div>
                      <div className={`text-sm font-medium ${isActive ? "text-white" : "text-forest-800"}`}>{item.label}</div>
                      <div className={`text-xs ${isActive ? "text-forest-200" : "text-forest-400"}`}>{item.desc}</div>
                    </div>
                  </button>
                </div>
              );
            })}

            {/* Quick stats — only show when a class is selected */}
            {selectedClass && activeTab === "classes" && (
              <div className="mt-6 px-3 py-3 bg-cream-50 rounded-xl border border-cream-200">
                <p className="text-xs font-semibold text-forest-600 mb-2">{selectedClass.class_name}</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-forest-500">Check-ins</span>
                    <span className="font-semibold text-forest-700">{classCheckins.length}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-forest-500">Observations</span>
                    <span className="font-semibold text-forest-700">{classObservations.length}</span>
                  </div>
                  {selectedClass.student_count && (
                    <div className="flex justify-between text-xs">
                      <span className="text-forest-500">Students</span>
                      <span className="font-semibold text-forest-700">{selectedClass.student_count}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </nav>
        </aside>

        {/* ── Main Content ── */}
        <main className="flex-1 p-6 overflow-y-auto">
          <AnimatePresence mode="wait">

            {/* ════════════════════════════════════════════════════════════
                MY CLASSES TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === "classes" && !selectedClass && (
              <motion.div key="classes-list" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-forest-900">My Classes</h2>
                    <p className="text-forest-500 text-sm mt-0.5">Click a class to view check-ins and observations</p>
                  </div>
                  <Button onClick={() => setShowClassForm(true)} className="bg-forest-600 hover:bg-forest-700 text-white gap-2">
                    <Plus className="w-4 h-4" /> Add Class
                  </Button>
                </div>

                {classes.length === 0 && !showClassForm && (
                  <Card className="border-2 border-dashed border-forest-200 bg-forest-50/50">
                    <CardContent className="py-16 text-center space-y-4">
                      <div className="w-16 h-16 bg-forest-100 rounded-2xl flex items-center justify-center mx-auto">
                        <Users className="w-8 h-8 text-forest-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-forest-800">No classes yet</h3>
                        <p className="text-forest-500 text-sm mt-1">Create your first class to start tracking SEL growth</p>
                      </div>
                      <Button onClick={() => setShowClassForm(true)} className="bg-forest-600 hover:bg-forest-700 text-white gap-2">
                        <Plus className="w-4 h-4" /> Create My Class
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Class Form */}
                <AnimatePresence>
                  {showClassForm && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                      <Card className="border-forest-200 shadow-sm">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-forest-800 text-lg">New Class</CardTitle>
                          <CardDescription>Fill in your class details — only the name is required</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2 space-y-1.5">
                              <Label className="text-forest-700 font-medium">Class Name *</Label>
                              <Input
                                placeholder="e.g. Room 4 – Morning Group"
                                value={classForm.class_name}
                                onChange={e => setClassForm(p => ({ ...p, class_name: e.target.value }))}
                                className="border-forest-200 focus:border-forest-400"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-forest-700 font-medium">Grade Level</Label>
                              <Input
                                placeholder="e.g. Kindergarten"
                                value={classForm.grade_level}
                                onChange={e => setClassForm(p => ({ ...p, grade_level: e.target.value }))}
                                className="border-forest-200 focus:border-forest-400"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-forest-700 font-medium">Number of Students</Label>
                              <Input
                                type="number"
                                placeholder="e.g. 22"
                                value={classForm.student_count}
                                onChange={e => setClassForm(p => ({ ...p, student_count: e.target.value }))}
                                className="border-forest-200 focus:border-forest-400"
                              />
                            </div>
                            <div className="col-span-2 space-y-1.5">
                              <Label className="text-forest-700 font-medium">School Name</Label>
                              <Input
                                placeholder="e.g. Lincoln Elementary"
                                value={classForm.school_name}
                                onChange={e => setClassForm(p => ({ ...p, school_name: e.target.value }))}
                                className="border-forest-200 focus:border-forest-400"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setShowClassForm(false)}>Cancel</Button>
                            <Button onClick={handleCreateClass} className="bg-forest-600 hover:bg-forest-700 text-white gap-2">
                              <Save className="w-4 h-4" /> Save Class
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Class Cards */}
                <div className="grid gap-4">
                  {classes.map(cls => (
                    <motion.div key={cls.id} whileHover={{ y: -1 }}>
                      <Card
                        className="border-2 border-forest-100 hover:border-forest-300 cursor-pointer transition-all"
                        onClick={() => loadClassDetail(cls)}
                      >
                        <CardContent className="p-5 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-forest-100 rounded-xl flex items-center justify-center">
                              <Users className="w-6 h-6 text-forest-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-forest-900">{cls.class_name}</h3>
                              <div className="flex items-center gap-3 mt-1">
                                {cls.grade_level && <span className="text-xs text-forest-500">{cls.grade_level}</span>}
                                {cls.school_name && <span className="text-xs text-forest-400">· {cls.school_name}</span>}
                                {cls.student_count && <span className="text-xs text-forest-400">· {cls.student_count} students</span>}
                              </div>
                              <span className="text-xs text-forest-400">{cls.academic_year}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-forest-100 text-forest-700 border-forest-200">Active</Badge>
                            <ChevronRight className="w-4 h-4 text-forest-400" />
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* ════════════════════════════════════════════════════════════
                CLASS DETAIL VIEW
            ════════════════════════════════════════════════════════════ */}
            {activeTab === "classes" && selectedClass && (
              <motion.div key="class-detail" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-6">

                {/* Back + class header */}
                <div>
                  <button
                    onClick={() => { setSelectedClass(null); setShowCheckinForm(false); setShowObsForm(false); }}
                    className="flex items-center gap-1.5 text-sm text-forest-500 hover:text-forest-700 mb-3 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to My Classes
                  </button>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-forest-100 rounded-xl flex items-center justify-center">
                        <Users className="w-6 h-6 text-forest-600" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-forest-900">{selectedClass.class_name}</h2>
                        <div className="flex items-center gap-3 mt-0.5">
                          {selectedClass.grade_level && <span className="text-sm text-forest-500">{selectedClass.grade_level}</span>}
                          {selectedClass.school_name && <span className="text-sm text-forest-400">· {selectedClass.school_name}</span>}
                          {selectedClass.student_count && <span className="text-sm text-forest-400">· {selectedClass.student_count} students</span>}
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      {!showCheckinForm && !showObsForm && (
                        <>
                          <Button
                            onClick={() => { setShowCheckinForm(true); setShowObsForm(false); }}
                            variant="outline"
                            className="border-forest-200 text-forest-600 hover:bg-forest-50 gap-2"
                            size="sm"
                          >
                            <ClipboardCheck className="w-4 h-4" /> New Check-In
                          </Button>
                          <Button
                            onClick={() => { setShowObsForm(true); setShowCheckinForm(false); }}
                            className="bg-forest-600 hover:bg-forest-700 text-white gap-2"
                            size="sm"
                          >
                            <Eye className="w-4 h-4" /> New Observation
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Check-In Form ── */}
                <AnimatePresence>
                  {showCheckinForm && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <Card className="border-forest-200 shadow-sm">
                        <CardHeader className="pb-4 border-b border-forest-100">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-forest-800">New Check-In</CardTitle>
                              <CardDescription className="mt-1">{selectedClass.class_name} · {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-sm text-forest-600">Period:</Label>
                              <select
                                value={checkinPeriod}
                                onChange={e => setCheckinPeriod(e.target.value)}
                                className="text-sm border border-forest-200 rounded-lg px-2 py-1 text-forest-700 bg-white"
                              >
                                <option value="monthly">Monthly</option>
                                <option value="beginning">Beginning of Year</option>
                                <option value="middle">Middle of Year</option>
                                <option value="end">End of Year</option>
                              </select>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-5 space-y-4">
                          {DOMAINS.map(domain => {
                            const Icon = domain.icon;
                            const rating = checkinRatings[domain.key] || 0;
                            const note = checkinNotes[domain.key] || "";
                            const showNote = visibleNotes[domain.key] || false;

                            return (
                              <div key={domain.key} className={`rounded-xl border-2 ${domain.border} bg-gradient-to-r ${domain.color} p-4`}>
                                <div className="flex items-start gap-3">
                                  <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                                    <Icon className={`w-5 h-5 ${domain.iconColor}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-semibold text-forest-900 text-sm">{domain.label}</span>
                                      <span className="text-xs text-forest-500">{domain.subtitle}</span>
                                    </div>
                                    <p className="text-xs text-forest-600 mt-0.5">{domain.description}</p>

                                    {/* Rating buttons */}
                                    <div className="flex gap-2 mt-3">
                                      {DOMAIN_OPTIONS.map(opt => {
                                        const OptIcon = opt.icon;
                                        const isSelected = rating === opt.value;
                                        return (
                                          <button
                                            key={opt.value}
                                            onClick={() => setCheckinRatings(p => ({ ...p, [domain.key]: opt.value }))}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${
                                              isSelected
                                                ? opt.value === 1 ? "bg-gray-200 border-gray-400 text-gray-700"
                                                  : opt.value === 2 ? "bg-golden-200 border-golden-400 text-golden-800"
                                                  : "bg-forest-200 border-forest-400 text-forest-800"
                                                : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                                            }`}
                                          >
                                            <OptIcon className="w-3.5 h-3.5" />
                                            {opt.label}
                                          </button>
                                        );
                                      })}

                                      {/* Add note button — preserved exactly from v3 */}
                                      <button
                                        onClick={() => toggleNote(domain.key)}
                                        className="ml-auto flex items-center gap-1 text-xs text-forest-500 hover:text-forest-700 px-2 py-1.5 rounded-lg hover:bg-white/60 transition-colors"
                                      >
                                        <Pencil className="w-3 h-3" />
                                        {note ? "Edit note" : "Add note"}
                                      </button>
                                    </div>

                                    {/* Inline note — preserved exactly from v3 */}
                                    <AnimatePresence>
                                      {(showNote || note) && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-2 overflow-hidden">
                                          <Textarea
                                            placeholder={`Note for ${domain.label}...`}
                                            value={note}
                                            onChange={e => setCheckinNotes(p => ({ ...p, [domain.key]: e.target.value }))}
                                            rows={2}
                                            className="text-xs bg-white/80 border-white resize-none"
                                          />
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* General notes */}
                          <div className="rounded-xl border-2 border-cream-300 bg-cream-50 p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <MessageCircle className="w-4 h-4 text-forest-500" />
                              <span className="font-medium text-forest-800 text-sm">General Notes</span>
                              <span className="text-xs text-forest-400">(optional)</span>
                            </div>
                            <Textarea
                              placeholder="Overall class observations, patterns you noticed, standout moments..."
                              value={checkinGeneralNotes}
                              onChange={e => setCheckinGeneralNotes(e.target.value)}
                              rows={3}
                              className="bg-white border-cream-200 resize-none text-sm"
                            />
                          </div>

                          <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => { setShowCheckinForm(false); setCheckinRatings({}); setCheckinNotes({}); setCheckinGeneralNotes(""); setVisibleNotes({}); }}>
                              Cancel
                            </Button>
                            <Button onClick={handleSaveCheckin} disabled={savingCheckin} className="bg-forest-600 hover:bg-forest-700 text-white gap-2">
                              {savingCheckin ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                              Save Check-In
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* ── Observation Form ── */}
                <AnimatePresence>
                  {showObsForm && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <Card className="border-forest-200 shadow-sm">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-forest-800 text-lg">New Observation</CardTitle>
                          <CardDescription>Capture a quote or moment from today</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-forest-700 font-medium">Which domain? *</Label>
                            <div className="grid grid-cols-4 gap-2">
                              {DOMAINS.map(d => (
                                <button
                                  key={d.key}
                                  onClick={() => setObsForm(p => ({ ...p, domain: d.key }))}
                                  className={`p-2 rounded-lg border-2 text-xs font-medium transition-all text-center ${
                                    obsForm.domain === d.key
                                      ? `${d.badgeColor} border-current`
                                      : "border-gray-200 text-gray-500 hover:border-gray-300 bg-white"
                                  }`}
                                >
                                  {d.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-forest-700 font-medium">Quote <span className="text-forest-400 font-normal">(child's words)</span></Label>
                            <Textarea
                              placeholder="&quot;It's okay to feel sad. My body just needs a moment.&quot;"
                              value={obsForm.quote}
                              onChange={e => setObsForm(p => ({ ...p, quote: e.target.value }))}
                              rows={2}
                              className="border-forest-200 resize-none"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-forest-700 font-medium">Context <span className="text-forest-400 font-normal">(what was happening)</span></Label>
                            <Textarea
                              placeholder="During morning circle after reading Chapter 2..."
                              value={obsForm.context}
                              onChange={e => setObsForm(p => ({ ...p, context: e.target.value }))}
                              rows={2}
                              className="border-forest-200 resize-none"
                            />
                          </div>

                          <div className="flex justify-end gap-2 pt-1">
                            <Button variant="outline" onClick={() => { setShowObsForm(false); setObsForm({ domain: "", quote: "", context: "" }); }}>Cancel</Button>
                            <Button onClick={handleSaveObservation} disabled={savingObs} className="bg-forest-600 hover:bg-forest-700 text-white gap-2">
                              {savingObs ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                              Save
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* ── Check-Ins list ── */}
                {!showCheckinForm && !showObsForm && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-forest-800 flex items-center gap-2">
                      <ClipboardCheck className="w-5 h-5 text-forest-500" />
                      Check-Ins
                      <Badge variant="outline" className="text-xs border-forest-200 text-forest-500 ml-1">{classCheckins.length}</Badge>
                    </h3>

                    {classCheckins.length === 0 && (
                      <Card className="border-2 border-dashed border-forest-200 bg-forest-50/30">
                        <CardContent className="py-10 text-center space-y-2">
                          <ClipboardCheck className="w-8 h-8 text-forest-300 mx-auto" />
                          <p className="text-forest-500 text-sm">No check-ins yet — click New Check-In above</p>
                        </CardContent>
                      </Card>
                    )}

                    <div className="space-y-3">
                      {classCheckins.map(ci => (
                        <motion.div key={ci.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                          <Card className="border-forest-100 hover:border-forest-200 transition-colors">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-forest-400" />
                                  <span className="font-semibold text-forest-800">{formatDate(ci.checkin_date)}</span>
                                  <Badge variant="outline" className="text-xs border-forest-200 text-forest-600 capitalize">{ci.checkin_period}</Badge>
                                </div>
                              </div>
                              <div className="grid grid-cols-7 gap-1.5">
                                {DOMAINS.map(d => {
                                  const val = ci[d.key as keyof CheckIn] as number;
                                  return (
                                    <div key={d.key} className="text-center">
                                      <div className={`text-xs font-bold mb-0.5 ${getRatingColor(val)}`}>{getRatingLabel(val)}</div>
                                      <div className={`text-xs ${d.badgeColor} rounded-md px-1 py-0.5 truncate`}>{d.label.split(" ")[0]}</div>
                                    </div>
                                  );
                                })}
                              </div>
                              {ci.notes && (
                                <div className="mt-3 pt-3 border-t border-forest-100">
                                  <p className="text-xs text-forest-600 line-clamp-2">{ci.notes}</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </div>

                    {/* ── Observations list ── */}
                    <h3 className="text-lg font-semibold text-forest-800 flex items-center gap-2 mt-6">
                      <Eye className="w-5 h-5 text-forest-500" />
                      Observations
                      <Badge variant="outline" className="text-xs border-forest-200 text-forest-500 ml-1">{classObservations.length}</Badge>
                    </h3>

                    {classObservations.length === 0 && (
                      <Card className="border-2 border-dashed border-forest-200 bg-forest-50/30">
                        <CardContent className="py-10 text-center space-y-2">
                          <Eye className="w-8 h-8 text-forest-300 mx-auto" />
                          <p className="text-forest-500 text-sm">No observations yet — click New Observation above</p>
                        </CardContent>
                      </Card>
                    )}

                    <div className="space-y-3">
                      {classObservations.map(obs => {
                        const domain = DOMAINS.find(d => d.key === obs.domain);
                        const Icon = domain?.icon || Star;
                        return (
                          <motion.div key={obs.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            <Card className="border-forest-100 hover:border-forest-200 transition-colors">
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${domain?.color || "from-gray-100 to-gray-200"}`}>
                                      <Icon className={`w-4 h-4 ${domain?.iconColor || "text-gray-500"}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <Badge className={`text-xs ${domain?.badgeColor || "bg-gray-100 text-gray-700"}`}>{domain?.label || obs.domain}</Badge>
                                        <span className="text-xs text-forest-400">{formatDate(obs.observation_date)}</span>
                                      </div>
                                      {obs.quote && (
                                        <blockquote className="text-sm text-forest-800 italic border-l-2 border-forest-300 pl-3 mb-1">
                                          "{obs.quote}"
                                        </blockquote>
                                      )}
                                      {obs.context && (
                                        <p className="text-xs text-forest-500 mt-1">{obs.context}</p>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteObservation(obs.id)}
                                    className="text-gray-300 hover:text-coral-400 transition-colors flex-shrink-0 mt-0.5"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* ════════════════════════════════════════════════════════════
                GROWTH TAB — unchanged from v3
            ════════════════════════════════════════════════════════════ */}
            {activeTab === "growth" && (
              <motion.div key="growth" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-forest-900">Growth Over Time</h2>
                    <p className="text-forest-500 text-sm mt-0.5">How your class is progressing across Dira domains</p>
                  </div>
                  {classes.length > 1 && (
                    <div className="flex items-center gap-1.5 bg-forest-50 rounded-lg px-3 py-1.5 border border-forest-100">
                      <Users className="w-3.5 h-3.5 text-forest-500" />
                      <select
                        value={growthActiveClass?.id || ""}
                        onChange={e => {
                          const cls = classes.find(c => c.id === e.target.value);
                          if (cls) { setGrowthActiveClass(cls); }
                        }}
                        className="text-sm text-forest-700 bg-transparent border-none outline-none cursor-pointer"
                      >
                        {classes.map(c => <option key={c.id} value={c.id}>{c.class_name}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {allCheckins.length < 2 && (
                  <Card className="border-2 border-dashed border-forest-200 bg-forest-50/30">
                    <CardContent className="py-14 text-center space-y-3">
                      <TrendingUp className="w-10 h-10 text-forest-300 mx-auto" />
                      <p className="text-forest-600 font-medium">Need at least 2 check-ins to see growth</p>
                      <p className="text-forest-400 text-sm">Complete your first check-in inside a class to get started</p>
                      <Button onClick={() => setActiveTab("classes")} variant="outline" className="border-forest-300 text-forest-600 gap-2">
                        <Users className="w-4 h-4" /> Go to My Classes
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {allCheckins.length >= 1 && (
                  <>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                      {DOMAINS.map(domain => {
                        const vals = allCheckins.map(ci => ci[domain.key as keyof CheckIn] as number).filter(Boolean);
                        const latest = vals[0] || 0;
                        const prev = vals[1] || 0;
                        const trend = latest - prev;
                        const Icon = domain.icon;
                        return (
                          <Card key={domain.key} className={`border-2 ${domain.border}`}>
                            <CardContent className="p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${domain.color} flex items-center justify-center`}>
                                  <Icon className={`w-4 h-4 ${domain.iconColor}`} />
                                </div>
                                <span className="text-xs font-semibold text-forest-700 leading-tight">{domain.label}</span>
                              </div>
                              <div className={`text-lg font-bold ${getRatingColor(latest)}`}>{getRatingLabel(latest)}</div>
                              {allCheckins.length >= 2 && (
                                <div className={`text-xs mt-0.5 ${trend > 0 ? "text-forest-500" : trend < 0 ? "text-coral-500" : "text-gray-400"}`}>
                                  {trend > 0 ? "↑ Improving" : trend < 0 ? "↓ Needs attention" : "→ Steady"}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    <Card className="border-forest-100">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-forest-800 text-base">Check-In History</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {allCheckins.slice().reverse().map((ci, idx) => (
                            <div key={ci.id} className="flex gap-4">
                              <div className="flex flex-col items-center">
                                <div className="w-8 h-8 bg-forest-100 rounded-full flex items-center justify-center text-xs font-bold text-forest-600">
                                  {idx + 1}
                                </div>
                                {idx < allCheckins.length - 1 && <div className="w-0.5 flex-1 bg-forest-100 mt-1" />}
                              </div>
                              <div className="flex-1 pb-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-semibold text-forest-800">{formatDate(ci.checkin_date)}</span>
                                  <Badge variant="outline" className="text-xs border-forest-200 text-forest-500 capitalize">{ci.checkin_period}</Badge>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {DOMAINS.map(d => {
                                    const val = ci[d.key as keyof CheckIn] as number;
                                    return (
                                      <span key={d.key} className={`text-xs px-2 py-0.5 rounded-full ${d.badgeColor}`}>
                                        {d.label.split(" ")[0]}: <span className="font-semibold">{getRatingLabel(val)}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                                {ci.notes && <p className="text-xs text-forest-500 mt-2 italic">{ci.notes}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </motion.div>
            )}

            {/* ════════════════════════════════════════════════════════════
                MY READINESS SCORE TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === "readiness" && (
              <motion.div key="readiness" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-forest-900">My Readiness Score</h2>
                  <p className="text-forest-500 text-sm mt-0.5">Your readiness trends over time</p>
                </div>

                {wellBeingEntries.length < 2 && (
                  <Card className="border-2 border-dashed border-forest-200 bg-forest-50/30">
                    <CardContent className="py-14 text-center space-y-3">
                      <BarChart3 className="w-10 h-10 text-forest-300 mx-auto" />
                      <p className="text-forest-600 font-medium">Need at least 2 check-ins to see trends</p>
                      <p className="text-forest-400 text-sm">Submit check-ins in My Readiness Meter to get started</p>
                      <Button onClick={() => setActiveTab("wellbeing")} variant="outline" className="border-forest-300 text-forest-600 gap-2">
                        <HeartPulse className="w-4 h-4" /> Go to My Readiness Meter
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {wellBeingEntries.length >= 2 && (
                  <>
                    {/* Score cards */}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {WELLBEING_SCALES.map(scale => {
                        const vals = wellBeingEntries.map(e => e[scale.id as keyof WellBeingEntry] as number).filter(Boolean);
                        const latest = vals[0] || 0;
                        const prev = vals[1] || 0;
                        const trend = latest - prev;
                        const Icon = scale.icon;
                        return (
                          <Card key={scale.id} className="border-forest-100">
                            <CardContent className="p-4 text-center">
                              <Icon className={`w-5 h-5 ${scale.color} mx-auto mb-2`} />
                              <p className="text-xs text-forest-500 font-medium mb-1">{scale.label}</p>
                              <p className="text-2xl font-bold text-forest-800">{latest}<span className="text-sm font-normal text-forest-400">/5</span></p>
                              <p className={`text-xs mt-1 ${trend > 0 ? "text-forest-500" : trend < 0 ? "text-coral-500" : "text-gray-400"}`}>
                                {trend > 0 ? `▲ +${trend}` : trend < 0 ? `▼ ${trend}` : "— steady"}
                              </p>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* History timeline */}
                    <div className="space-y-4">
                      {wellBeingEntries.map((entry, idx) => (
                        <motion.div key={entry.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                          <Card className="border-forest-100">
                            <CardContent className="p-4">
                              <p className="text-xs text-forest-400 mb-3">{formatDateTime(entry.created_at)}</p>
                              <div className="flex gap-4 flex-wrap">
                                {WELLBEING_SCALES.map(scale => {
                                  const val = entry[scale.id as keyof WellBeingEntry] as number;
                                  return (
                                    <div key={scale.id} className="flex flex-col items-center gap-1">
                                      <span className="text-2xl">{scale.emojis[val - 1]}</span>
                                      <span className="text-xs text-forest-500 font-medium">{scale.label}</span>
                                      <span className="text-xs text-forest-400">{val}/5</span>
                                    </div>
                                  );
                                })}
                              </div>
                              {entry.note && (
                                <p className="text-xs text-forest-600 mt-3 pt-3 border-t border-forest-100 italic">{entry.note}</p>
                              )}
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            )}

            {/* ════════════════════════════════════════════════════════════
                MY WELL-BEING TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === "wellbeing" && (
              <motion.div key="wellbeing" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-forest-900">My Readiness Meter</h2>
                    <p className="text-forest-500 text-sm mt-0.5">How ready are you for today?</p>
                  </div>
                  {!showWbForm && (
                    <Button onClick={() => setShowWbForm(true)} className="bg-forest-600 hover:bg-forest-700 text-white gap-2">
                      <Plus className="w-4 h-4" /> Check In
                    </Button>
                  )}
                </div>

                {/* Well-being form */}
                <AnimatePresence>
                  {showWbForm && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <Card className="border-forest-200 shadow-sm">
                        <CardHeader className="pb-4 border-b border-forest-100">
                          <CardTitle className="text-forest-800">How are you doing today?</CardTitle>
                          <CardDescription>Tap to rate each area — takes under a minute</CardDescription>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-8">
                          {WELLBEING_SCALES.map(scale => {
                            const Icon = scale.icon;
                            const selected = wbRatings[scale.id] || 0;
                            return (
                              <div key={scale.id}>
                                <div className="flex items-center gap-2 mb-4">
                                  <Icon className={`w-5 h-5 ${scale.color}`} />
                                  <span className="font-semibold text-forest-800">{scale.label}</span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs text-forest-400 w-20 text-right">{scale.low}</span>
                                  <div className="flex gap-3 flex-1 justify-center">
                                    {scale.emojis.map((emoji, i) => {
                                      const val = i + 1;
                                      const isSelected = selected === val;
                                      return (
                                        <button
                                          key={val}
                                          onClick={() => setWbRatings(p => ({ ...p, [scale.id]: val }))}
                                          className={`flex flex-col items-center gap-1 transition-all ${
                                            isSelected ? "scale-125" : "opacity-50 hover:opacity-80 hover:scale-110"
                                          }`}
                                        >
                                          <span className="text-3xl leading-none">{emoji}</span>
                                          <span className={`text-xs font-medium ${isSelected ? "text-forest-700" : "text-forest-400"}`}>{val}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <span className="text-xs text-forest-400 w-20">{scale.high}</span>
                                </div>
                              </div>
                            );
                          })}

                          {/* Optional note */}
                          <div className="pt-2">
                            <Label className="text-forest-700 font-medium text-sm mb-1.5 block">Anything on your mind? <span className="font-normal text-forest-400">(optional)</span></Label>
                            <Textarea
                              placeholder="A reflection, what's weighing on you, or what's going well..."
                              value={wbNote}
                              onChange={e => setWbNote(e.target.value)}
                              rows={3}
                              className="border-forest-200 resize-none text-sm"
                            />
                          </div>

                          <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => { setShowWbForm(false); setWbRatings({}); setWbNote(""); }}>Cancel</Button>
                            <Button onClick={handleSaveWellBeing} disabled={savingWb} className="bg-forest-600 hover:bg-forest-700 text-white gap-2">
                              {savingWb ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <HeartPulse className="w-4 h-4" />}
                              Save Check-In
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Past well-being entries */}
                {wellBeingEntries.length === 0 && !showWbForm && (
                  <Card className="border-2 border-dashed border-forest-200 bg-forest-50/30">
                    <CardContent className="py-14 text-center space-y-3">
                      <HeartPulse className="w-10 h-10 text-forest-300 mx-auto" />
                      <p className="text-forest-600 font-medium">No check-ins yet</p>
                      <p className="text-forest-400 text-sm">Your well-being history will appear here</p>
                    </CardContent>
                  </Card>
                )}

                <div className="space-y-3">
                  {wellBeingEntries.map(entry => (
                    <motion.div key={entry.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                      <Card className="border-forest-100">
                        <CardContent className="p-4">
                          <p className="text-xs text-forest-400 mb-3">{formatDateTime(entry.created_at)}</p>
                          <div className="flex gap-6">
                            {WELLBEING_SCALES.map(scale => {
                              const val = entry[scale.id as keyof WellBeingEntry] as number;
                              return (
                                <div key={scale.id} className="flex flex-col items-center gap-1">
                                  <span className="text-2xl">{scale.emojis[val - 1]}</span>
                                  <span className="text-xs text-forest-500 font-medium">{scale.label}</span>
                                  <span className="text-xs text-forest-400">{val}/5</span>
                                </div>
                              );
                            })}
                          </div>
                          {entry.note && (
                            <p className="text-xs text-forest-600 mt-3 pt-3 border-t border-forest-100 italic">{entry.note}</p>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* ════════════════════════════════════════════════════════════
                STAYING DIRA CURRICULUM TAB — LANDING
            ════════════════════════════════════════════════════════════ */}
            {activeTab === "curriculum" && selectedCurriculumChapter === null && (
              <motion.div key="curriculum-landing" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">

                {/* Header */}
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-forest-900">Staying Dira</h2>
                    <p className="text-forest-500 text-sm mt-0.5">The Living Classroom Curriculum · 18 Weeks · PreK–Grade 2 · Two Curriculum Options: Flexible or Structured</p>
                  </div>
                  <div className="flex items-center gap-1 bg-forest-50 border border-forest-200 rounded-xl p-1 no-print">
                    <button
                      onClick={() => setCurriculumStyle("flexible")}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${curriculumStyle === "flexible" ? "bg-forest-600 text-white shadow-sm" : "text-forest-600 hover:bg-forest-100"}`}
                    >
                      Flexible
                    </button>
                    <button
                      onClick={() => setCurriculumStyle("structured")}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${curriculumStyle === "structured" ? "bg-forest-600 text-white shadow-sm" : "text-forest-600 hover:bg-forest-100"}`}
                    >
                      Structured
                    </button>
                  </div>
                </div>

                <Card className="border-forest-100 bg-cream-50">
                  <CardContent className="p-4">
                    {curriculumStyle === "structured" ? (
                      <p className="text-sm text-forest-700"><strong>Structured:</strong> Day-by-day activities for each week. Best for teachers who want a clear daily plan to follow.</p>
                    ) : (
                      <p className="text-sm text-forest-700"><strong>Flexible:</strong> Weekly intentions and prompts you weave into your day naturally. Best for teachers who want to internalize the language and use it as it fits.</p>
                    )}
                  </CardContent>
                </Card>

                <div className="grid grid-cols-3 gap-3">
                  {[
                    { icon: "📖", title: "The Book Layer", desc: "Reading chapters on the Dira platform, activities, and discussion prompts. Already exists — you're building on it." },
                    { icon: "🌱", title: "The Living Layer", desc: "How you carry each domain beyond the book into the rest of your day — through language, micro-rituals, and responsive cues." },
                    { icon: "🏡", title: "The Home Bridge", desc: "Keeping families in sync so the language doesn't stop at the classroom door. Dira Mission for grown-ups, Dira Dare for kids." },
                  ].map(layer => (
                    <Card key={layer.title} className="border-forest-100">
                      <CardContent className="p-4">
                        <div className="text-2xl mb-2">{layer.icon}</div>
                        <p className="font-semibold text-forest-800 text-sm mb-1">{layer.title}</p>
                        <p className="text-sm text-forest-500 leading-relaxed">{layer.desc}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card className="border-golden-200 bg-golden-50/40">
                  <CardContent className="p-4 flex gap-3">
                    <span className="text-xl mt-0.5">✨</span>
                    <div>
                      <p className="font-semibold text-forest-800 text-sm">The 2+1 Week Arc</p>
                      <p className="text-sm text-forest-600 mt-1 leading-relaxed">Each of the 6 chapters gets 3 weeks. <strong>Week 1: Introduce.</strong> <strong>Week 2: Deepen.</strong> <strong>Week 3: Live It</strong> — no new content, just language and presence. Week 3 is where the magic happens.</p>
                      <p className="text-sm text-forest-500 mt-2">Click on a chapter below to review the curriculum.</p>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-3 gap-2 max-w-lg mx-auto">
                  {CURRICULUM_CHAPTERS.map((ch, i) => (
                    <button
                      key={i}
                      onClick={() => { setSelectedCurriculumChapter(i); setActiveCurriculumChapter(i); }}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium border border-forest-200 text-forest-600 hover:bg-forest-50 transition-all text-center"
                    >
                      {ch.emoji} {ch.title}
                    </button>
                  ))}
                </div>

              </motion.div>
            )}

            {/* ════════════════════════════════════════════════════════════
                STAYING DIRA CURRICULUM TAB — CHAPTER DETAIL
            ════════════════════════════════════════════════════════════ */}
            {activeTab === "curriculum" && selectedCurriculumChapter !== null && (
              <motion.div key="curriculum-chapter" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">

                {/* Back + Print row */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setSelectedCurriculumChapter(null)}
                    className="no-print flex items-center gap-1.5 text-sm text-forest-500 hover:text-forest-700 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to Curriculum
                  </button>
                  <div className="flex items-center gap-2 no-print">
                    <button
                      onClick={() => window.print()}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-forest-200 text-forest-600 text-sm hover:bg-forest-50 transition-colors"
                    >
                      🖨️ Print
                    </button>
                    <div className="flex items-center gap-1 bg-forest-50 border border-forest-200 rounded-xl p-1">
                      <button
                        onClick={() => setCurriculumStyle("flexible")}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${curriculumStyle === "flexible" ? "bg-forest-600 text-white shadow-sm" : "text-forest-600 hover:bg-forest-100"}`}
                      >
                        Flexible
                      </button>
                      <button
                        onClick={() => setCurriculumStyle("structured")}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${curriculumStyle === "structured" ? "bg-forest-600 text-white shadow-sm" : "text-forest-600 hover:bg-forest-100"}`}
                      >
                        Structured
                      </button>
                    </div>
                  </div>
                </div>

                {(() => {
                  const ch = CURRICULUM_CHAPTERS[selectedCurriculumChapter];
                  const content = curriculumStyle === "structured" ? ch.structured : ch.flexible;
                  return (
                    <div className="space-y-4">

                      {/* Chapter header */}
                      <Card className={`border-2 ${ch.border}`}>
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            <span className="text-4xl">{ch.emoji}</span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-lg font-bold text-forest-900">{ch.title}</h3>
                                <Badge className={ch.badge}>{ch.domain}</Badge>
                                <Badge variant="outline" className="border-forest-200 text-forest-500 text-sm">Cue: {ch.cue}</Badge>
                              </div>
                              <p className="text-sm text-forest-600 mt-2 leading-relaxed"><strong>Big Idea:</strong> {ch.bigIdea}</p>
                              <p className="text-sm text-forest-400 mt-1 italic">You Matter: {ch.youMatter}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Learning goals */}
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-forest-800">Learning Goals</p>
                        <div className="grid grid-cols-2 gap-4">
                        <Card className="border-forest-100">
                          <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm text-forest-700">For Kids</CardTitle></CardHeader>
                          <CardContent className="px-4 pb-4">
                            <ul className="space-y-1.5">{ch.goalsKids.map((g, i) => <li key={i} className="text-sm text-forest-600 flex gap-2"><span className="text-forest-400 mt-0.5">•</span>{g}</li>)}</ul>
                          </CardContent>
                        </Card>
                        <Card className="border-forest-100">
                          <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm text-forest-700">For Teachers</CardTitle></CardHeader>
                          <CardContent className="px-4 pb-4">
                            <ul className="space-y-1.5">{ch.goalsTeachers.map((g, i) => <li key={i} className="text-sm text-forest-600 flex gap-2"><span className="text-forest-400 mt-0.5">•</span>{g}</li>)}</ul>
                          </CardContent>
                        </Card>
                      </div>
                      </div>

                      {/* Tactile touchpoints */}
                      <Card className="border-forest-200 bg-forest-50/30">
                        <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm text-forest-700">👐 Tactile Touchpoints — introduce Week 1, use all 3 weeks</CardTitle></CardHeader>
                        <CardContent className="px-4 pb-4">
                          <ul className="space-y-1.5 mb-2">{ch.tactiletouchpoints.map((t, i) => <li key={i} className="text-sm text-forest-600 flex gap-2"><span className="text-forest-400 mt-0.5">•</span>{t}</li>)}</ul>
                          <p className="text-xs text-forest-400 italic">{ch.tactileNote}</p>
                        </CardContent>
                      </Card>

                      {/* 3-week arc */}
                      <div className="space-y-3">
                        {/* Week 1 */}
                        <Card className="border-2 border-blue-200 bg-blue-50/40">
                          <CardHeader className="pb-2 pt-4 px-4">
                            <CardTitle className="text-sm text-forest-800">Week 1 — Introduce</CardTitle>
                            <p className="text-xs text-forest-500 italic mt-0.5">{content.week1intro}</p>
                          </CardHeader>
                          <CardContent className="px-4 pb-4">
                            <ul className="space-y-2">{content.week1.map((item, i) => <li key={i} className="text-sm text-forest-600 leading-relaxed flex gap-2"><span className="text-forest-300 mt-0.5 flex-shrink-0">›</span><span>{item}</span></li>)}</ul>
                          </CardContent>
                        </Card>

                        {/* Week 2 */}
                        <Card className="border-2 border-golden-200 bg-golden-50/40">
                          <CardHeader className="pb-2 pt-4 px-4">
                            <CardTitle className="text-sm text-forest-800">Week 2 — Deepen</CardTitle>
                            <p className="text-xs text-forest-500 italic mt-0.5">{content.week2intro}</p>
                          </CardHeader>
                          <CardContent className="px-4 pb-4 space-y-3">
                            {content.week2framework && (
                              <div className="bg-golden-100/60 border border-golden-200 rounded-lg p-3">
                                <p className="text-xs font-semibold text-golden-800 mb-1">📋 Week 2 Framework</p>
                                {content.week2framework.split('\n\n').map((line, i) => (
                                  <p key={i} className="text-xs text-golden-700 leading-relaxed mb-0.5">{line}</p>
                                ))}
                              </div>
                            )}
                            <ul className="space-y-2">{content.week2.map((item, i) => <li key={i} className="text-sm text-forest-600 leading-relaxed flex gap-2"><span className="text-forest-300 mt-0.5 flex-shrink-0">›</span><span>{item}</span></li>)}</ul>
                          </CardContent>
                        </Card>

                        {/* Week 3 */}
                        <Card className="border-2 border-forest-200 bg-forest-50/40">
                          <CardHeader className="pb-2 pt-4 px-4">
                            <CardTitle className="text-sm text-forest-800">Week 3 — Live It</CardTitle>
                            <p className="text-xs text-forest-500 italic mt-0.5">{content.week3intro}</p>
                          </CardHeader>
                          <CardContent className="px-4 pb-4 space-y-3">
                            <div className="bg-forest-100/60 border border-forest-200 rounded-lg p-3">
                              <p className="text-xs font-semibold text-forest-800 mb-2">🌿 The Three Moves</p>
                              {content.week3moves.split('\n\n').map((move, i) => (
                                <p key={i} className="text-xs text-forest-700 leading-relaxed mb-1">{move}</p>
                              ))}
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-forest-700 mb-1.5">Natural language prompts:</p>
                              <ul className="space-y-1.5">{content.week3.map((item, i) => <li key={i} className="text-sm text-forest-600 leading-relaxed flex gap-2"><span className="text-forest-300 mt-0.5 flex-shrink-0">›</span><span>{item}</span></li>)}</ul>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Do This / Not This */}
                      <div className="grid grid-cols-2 gap-4">
                        <Card className="border-forest-200 bg-forest-50/30">
                          <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm text-forest-700">✓ Do This</CardTitle></CardHeader>
                          <CardContent className="px-4 pb-4">
                            <ul className="space-y-1.5">{ch.doThis.map((d, i) => <li key={i} className="text-sm text-forest-600 flex gap-2"><span className="text-forest-400 mt-0.5">•</span>{d}</li>)}</ul>
                          </CardContent>
                        </Card>
                        <Card className="border-coral-100 bg-coral-50/20">
                          <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm text-coral-700">✗ Not This</CardTitle></CardHeader>
                          <CardContent className="px-4 pb-4">
                            <ul className="space-y-1.5">{ch.notThis.map((d, i) => <li key={i} className="text-sm text-forest-600 flex gap-2"><span className="text-coral-300 mt-0.5">•</span>{d}</li>)}</ul>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Home Bridge — 3 weeks */}
                      <Card className="border-golden-200 bg-golden-50/30">
                        <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm text-forest-800">🏡 Home Bridge</CardTitle></CardHeader>
                        <CardContent className="px-4 pb-4 space-y-4">
                          {ch.homeBridge.map((wk, i) => (
                            <div key={i} className={i > 0 ? "pt-4 border-t border-golden-100" : ""}>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-semibold text-golden-800">{wk.week}</p>
                                <button
                                  onClick={() => setSendHomeModal({ chapter: ch, week: wk })}
                                  className="no-print flex items-center gap-1 text-xs text-forest-600 border border-forest-200 rounded-lg px-2 py-1 hover:bg-forest-50 transition-colors"
                                >
                                  📤 Send Home
                                </button>
                              </div>
                              <div className="space-y-2">
                                {wk.days.map((day, di) => (
                                  <div key={di} className="bg-white border border-golden-100 rounded-lg p-3">
                                    <p className="text-xs font-semibold text-golden-700 mb-1.5">{day.day}</p>
                                    {day.mission && (
                                      <div className="mb-1.5">
                                        <span className="text-xs font-semibold text-forest-700">🏠 Dira Mission: </span>
                                        <span className="text-sm text-forest-600">{day.mission}</span>
                                      </div>
                                    )}
                                    {day.conversation && (
                                      <div className="mb-1.5">
                                        <p className="text-xs font-semibold text-forest-700 mb-0.5">💬 Conversation starters:</p>
                                        <ul className="space-y-0.5">{day.conversation.map((q, qi) => <li key={qi} className="text-sm text-forest-600 flex gap-1.5"><span className="text-forest-300 mt-0.5">›</span>{q}</li>)}</ul>
                                      </div>
                                    )}
                                    {day.dare && (
                                      <div>
                                        <span className="text-xs font-semibold text-forest-700">🌟 Dira Dare: </span>
                                        <span className="text-sm text-forest-600">{day.dare}</span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </Card>

                      {/* Teacher reflection */}
                      <Card className="border-purple-100 bg-purple-50/20">
                        <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm text-forest-800">🪞 Teacher Reflection — After Week 3</CardTitle></CardHeader>
                        <CardContent className="px-4 pb-4">
                          <ul className="space-y-1.5">{ch.reflection.map((r, i) => <li key={i} className="text-sm text-forest-600 flex gap-2"><span className="text-purple-300 mt-0.5">•</span>{r}</li>)}</ul>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })()}

              </motion.div>
            )}

          </AnimatePresence>
        </main>
      </div>

      {/* ── Send Home Modal ── */}
      {sendHomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm no-print" onClick={() => setSendHomeModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Modal header — hidden on print */}
            <div className="no-print flex items-center justify-between px-6 py-4 border-b border-forest-100">
              <p className="font-semibold text-forest-800">Home Bridge Sheet</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-forest-600 text-white text-sm hover:bg-forest-700 transition-colors"
                >
                  🖨️ Print
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-forest-200 text-forest-600 text-sm hover:bg-forest-50 transition-colors"
                >
                  ⬇️ Download PDF
                </button>
                <button onClick={() => setSendHomeModal(null)} className="text-forest-400 hover:text-forest-600 ml-1">✕</button>
              </div>
            </div>

            {/* Printable content */}
            <div id="send-home-print-area" className="px-8 py-6 space-y-5">
              {/* Branding */}
              <div className="flex items-center gap-3 pb-4 border-b-2 border-forest-200">
                <div className="w-10 h-10 bg-forest-600 rounded-full flex items-center justify-center text-white font-bold text-lg">D</div>
                <div>
                  <p className="font-bold text-forest-900 text-lg">Dira Stories</p>
                  <p className="text-forest-500 text-xs">Staying Dira · Home Bridge</p>
                </div>
              </div>

              {/* Chapter + week */}
              <div className="flex items-center gap-3">
                <span className="text-3xl">{sendHomeModal.chapter.emoji}</span>
                <div>
                  <p className="font-bold text-forest-900">{sendHomeModal.chapter.title}</p>
                  <p className="text-forest-500 text-sm">{sendHomeModal.week.week}</p>
                </div>
              </div>

              {/* Dira Mission / days content */}
              <div className="space-y-3">
                {sendHomeModal.week.days.map((day, i) => (
                  <div key={i} className="border border-golden-200 rounded-xl p-4 bg-golden-50/30">
                    <p className="text-xs font-bold text-golden-800 uppercase tracking-wide mb-2">{day.day}</p>
                    {day.mission && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-forest-700 mb-0.5">🏠 Dira Mission</p>
                        <p className="text-sm text-forest-800 leading-relaxed">{day.mission}</p>
                      </div>
                    )}
                    {day.conversation && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-forest-700 mb-0.5">💬 Conversation starters</p>
                        <ul className="space-y-0.5">{day.conversation.map((q, qi) => <li key={qi} className="text-sm text-forest-800 flex gap-1.5"><span className="text-forest-400">›</span>{q}</li>)}</ul>
                      </div>
                    )}
                    {day.dare && (
                      <div>
                        <p className="text-xs font-semibold text-forest-700 mb-0.5">🌟 Dira Dare (grown-ups)</p>
                        <p className="text-sm text-forest-800 leading-relaxed">{day.dare}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Footer */}
              <p className="text-center text-xs text-forest-400 pt-2">dirastories.com · Staying Dira Curriculum</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
